"use client";

import React, { useState, useEffect } from "react";
import { GroupPost, PolicyParagraph, EditProposal, Comment, PolicyVoteStats } from "@/types/billTypes";
import { Button } from "@/af_components/Button";
import {
    enrichPostWithAuthor,
    getMajorityNeeded,
    getPolicyDetails,
    voteDocument,
    fetchDocumentSignatures,
    getUserProfile,
    addPolicySection,
    handleSecondDoc,
    voteNewParagraph,
    signOrVetoDocument,
    vetoDocument,
    effectDocument,
    shiftParagraphPositions,
    vetoParagraph,
    effectParagraph,
    removeEffectDocument,
    removeEffectParagraph,
    getRationaleComments,
    getEditProposals,
    insertRationaleComment,
    submitEditProposal,
    updateRationaleComment,
    deleteRationaleComment,
    getDocumentVoters,
    getParagraphVoters,
    submitTitleAmendment
} from "@/af_actions/declarationActions";
import {
    ChevronUp, ChevronDown, Plus, X, ArrowLeft, PenTool, CheckCircle2,
    MessageSquare, StickyNote, BookA, Feather, User as UserIcon,
    ScrollIcon, MessageCircle, XCircle, Save, Trash2, Edit2, ThumbsUp, ThumbsDown,
    LetterText,
    Users,
    Star
} from "lucide-react";
import GoodMarkdownParser from "@/af_components/safeMarkedParser";
import { useRouter } from "next/navigation";
import { Show, useUser } from "@clerk/nextjs";
import { CustomSelect } from "./CustomSelect";
import Link from "next/link";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ShowIfRole } from "@/components/ShowIfRole";
import { fetchResolutionHistory, getClerkUserInfo } from "@/actions/declarationActions";
import PolicyList from "./AssemblyFilter";
import ExpandableComment from "./ExpandingParagraph";
import ExpandableParagraph from "./ExpandableParagraph";
import ExpandablePolicy from "./ExpandingPolicy";
import { AmendmentVoteCard } from "./AmendmentVoteCard";
import VoterList, { VotingRecordList } from "./VoterList";
import Markdown from "@/components/Markdown";

interface PolicyEditorProps {
    post: GroupPost;
    onBack: () => void;
    allowedPlans?: Array<'plus' | 'pro' | 'elite'>
    resolutionBasePath: string;
    allowAllSignedIn: boolean;
    title?: string;
}

interface CommitteeOption {
    id: string;
    name: string;
    slug: string;
    description?: string;
}

const DebateMiniView = () => {
    return <></>;
};

const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";

export const NewDeclarationEditor: React.FC<PolicyEditorProps> = ({ title = "General Congregation", post: initialPost, onBack, allowedPlans, resolutionBasePath = '/assembly/general-congregation', allowAllSignedIn = false }) => {
    const [post, setPost] = useState<GroupPost>(initialPost);
    const [paragraphs, setParagraphs] = useState<PolicyParagraph[]>(post.paragraphs || []);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedAmendments, setExpandedAmendments] = useState<Set<string>>(new Set());
    const [majorityNeeded, setMajorityNeeded] = useState(1);
    const [billStatus, setBillStatus] = useState((post as any).status || "active");
    const [username, setUsername] = useState("");
    const [userOffice, setUserOffice] = useState<string | null>(null);
    const [signatures, setSignatures] = useState<any[]>([]);
    const [voteStats, setVoteStats] = useState<PolicyVoteStats>(
        initialPost.voteStats || { upvotes: 0, downvotes: 0, userVote: null }
    );
    const [viewMode, setViewMode] = useState("general");

    const [showAddModal, setShowAddModal] = useState(false);
    const [newSectionContent, setNewSectionContent] = useState("");
    const [newSectionTitle, setNewSectionTitle] = useState("");
    const [newSectionRationale, setNewSectionRationale] = useState("");

    const PRESIDENT_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";
    const [showSignModal, setShowSignModal] = useState(false);
    const [presidentialAction, setPresidentialAction] = useState<'signed' | 'vetoed' | null>(null);
    const [presStatement, setPresStatement] = useState("");
    const [language, setLanguage] = useState<'english' | 'american'>('english');
    const [insertAtPosition, setInsertAtPosition] = useState<number | null>(null);
    const [openSectionDetails, setOpenSectionDetails] = useState<Map<string, 'comments' | 'amendments' | 'votes'>>(new Map());
    const [sectionComments, setSectionComments] = useState<Record<string, any[]>>({});
    const [sectionNewComment, setSectionNewComment] = useState('');
    const [sectionCommentFormSection, setSectionCommentFormSection] = useState<string | null>(null);
    const [isSubmittingSectionComment, setIsSubmittingSectionComment] = useState(false);
    const [sectionAmendments, setSectionAmendments] = useState<Record<string, EditProposal[]>>({});


    const [sectionAmendmentViewModes, setSectionAmendmentViewModes] = useState<Record<string, 'new' | 'all'>>({});
    const [sectionLoading, setSectionLoading] = useState<Record<string, boolean>>({});
    const [showSectionAmendModal, setShowSectionAmendModal] = useState<string | null>(null);
    const [amendSectionId, setAmendSectionId] = useState<string>('')
    const [amendSectionContent, setAmendSectionContent] = useState('');
    const [amendSectionTitle, setAmendSectionTitle] = useState('');
    const [amendSectionRationale, setAmendSectionRationale] = useState('');
    const [amendSectionTranslation, setAmendSectionTranslation] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [tempCommentContent, setTempCommentContent] = useState('');

    const { user: clerkUser } = useUser();
    const userId = clerkUser?.id;
    const router = useRouter();

    const [activeUser, setActiveUser] = useState();


    const [documentVoters, setDocumentVoters] = useState<any[]>([]);
    const [showDocVoters, setShowDocVoters] = useState(false);
    const [sectionVoters, setSectionVoters] = useState<Record<string, any[]>>({});
    const [isLoadingVoters, setIsLoadingVoters] = useState<Record<string, boolean>>({});

    const [documentDebate, setDocumentDebate] = useState<any[]>([]);
    const [showDocDebate, setShowDocDebate] = useState(false);

    const isAdmin = clerkUser?.id === ADMIN_ID;
    const descriptionSection = paragraphs.find((p) => p.position === 0);
    const normalParagraphs = paragraphs.filter((p) => p.position !== 0);

    let isGeneralAssembly: boolean;

    if (allowAllSignedIn) isGeneralAssembly = true;

    let isHolySeat: boolean;

    const accessPlans = allowedPlans ?? []//['plus', 'pro', 'elite'];
    const [hasRankAccess, setHasRankAccess] = useState(false);
    const hasChamberAccess = (has: any) => accessPlans.some((plan) => has({ plan })) || isAdmin || hasRankAccess;
    const chamberAccessLabel = accessPlans.includes('elite') ? 'elite' : accessPlans.includes('pro') ? 'pro' : 'plus';

    // if it's one of these, it's the holy seat
    const isGreatHall = chamberAccessLabel === "plus";
    const isGrandCouncil = chamberAccessLabel === "pro";
    const isConventionOfFathers = chamberAccessLabel === "elite";

    const getActiveUser = async (userId: string) => {
        let user = await getClerkUserInfo(userId);
        return user;
    }

    useEffect(() => {
        const getActiveUser = async (userId: string) => {
            let user = await getClerkUserInfo(userId);
            setActiveUser(user as any)
            console.log(user)
            console.log(user?.rank)
            console.log(hasChamberAccess)
            if (!hasRankAccess) {
                if (user?.rank === "Father") {
                    console.log("User rank is Father")
                    setHasRankAccess(true);
                }
            }
        }
        getActiveUser(clerkUser?.id);




    }, [clerkUser])

    useEffect(() => {

    }, [activeUser])


    if (isGreatHall || isGrandCouncil || isConventionOfFathers) isHolySeat = true;

    const chamberType = allowAllSignedIn ? "assembly" : isGreatHall ? 'plus' : isGrandCouncil ? 'pro' : isConventionOfFathers && 'elite';




    const [committeeOptions, setCommitteeOptions] = useState<CommitteeOption[]>([
        { id: 'general', name: 'General', slug: 'general', description: 'General Committee' }
    ]);
    const [selectedCommitteeSlug, setSelectedCommitteeSlug] = useState<string>(() => {
        const current = (initialPost.committee || 'General').trim().toLowerCase();
        if (current === 'general' || current === 'general committee') return 'general';
        return current.replace(/\s+/g, '-');
    });
    const [committeeLoading, setCommitteeLoading] = useState(false);
    const [committeeError, setCommitteeError] = useState<string | null>(null);
    const [editingCommittee, setEditingCommittee] = useState(false);

    const [documentHistory, setDocumentHistory] = useState<any[]>([]);
    const [sectionHistory, setSectionHistory] = useState<Record<string, any[]>>({});

    const getCommitteeSlugFromName = (committeeName?: string) => {
        if (!committeeName) return 'general';
        const normalized = committeeName.trim().toLowerCase();
        if (normalized === 'general' || normalized === 'general committee') return 'general';
        return normalized.replace(/\s+/g, '-');
    };

    const handleUpdateCommittee = async (slug: string) => {
        const selected = committeeOptions.find((option) => option.slug === slug) || committeeOptions[0];
        const committeeName = selected.slug === 'general' ? 'General' : selected.name;
        setSelectedCommitteeSlug(slug);
        setCommitteeError(null);

        try {
            const response = await fetch('/api/bills/update-committee', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: post.id, committeeName })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data?.error || 'Unable to update committee');
            setPost((prev) => ({ ...prev, committee: committeeName }));
            setEditingCommittee(false);
            return true;
        } catch (error) {
            console.error('Committee update failed', error);
            setCommitteeError('Failed to update committee.');
            return false;
        }
    };

    useEffect(() => {
        const loadCommittees = async () => {
            if (!isAdmin) return;
            setCommitteeLoading(true);
            setCommitteeError(null);

            try {
                const response = await fetch('/api/committees');
                const data = await response.json();
                if (!response.ok) throw new Error(data?.error || 'Failed to fetch committees');

                const options: CommitteeOption[] = [
                    { id: 'general', name: 'General', slug: 'general', description: 'General Committee' },
                    ...((Array.isArray(data) ? data : []) as CommitteeOption[])
                        .filter((committee) => committee.slug !== 'general')
                        .map((committee) => ({
                            id: committee.id,
                            name: committee.name,
                            slug: committee.slug,
                            description: committee.description
                        }))
                ];

                setCommitteeOptions(options);
                setSelectedCommitteeSlug(getCommitteeSlugFromName(initialPost.committee));
            } catch (err) {
                console.error('Committees load failed', err);
                setCommitteeError('Unable to load committee list.');
            } finally {
                setCommitteeLoading(false);
            }
        };

        loadCommittees();
    }, [initialPost.committee, isAdmin]);

    // 2. Author and Metadata enrichment
    useEffect(() => {
        const enrich = async () => {
            const enriched = await enrichPostWithAuthor(initialPost);
            setPost(enriched);
            const maj = await getMajorityNeeded();
            setMajorityNeeded(maj.majorityNeeded);
            if (userId) {
                const profile = await getUserProfile(userId);
                if (profile) {
                    setUsername(profile.username);
                    setUserOffice(profile.office);
                }
            }
        };
        //enrich();
    }, [initialPost, userId]);


    const handleAddSection = async () => {
        if (!newSectionContent.trim()) return;
        try {
            const targetPos = insertAtPosition !== null ? insertAtPosition : paragraphs.length;

            if (insertAtPosition !== null) {
                await shiftParagraphPositions(post.id, targetPos);
            }

            const newPara = await addPolicySection(
                post.id,
                language === 'english' ? newSectionContent : '',
                newSectionRationale, //|| "Original section.",
                targetPos,
                language === 'american' ? newSectionContent : '',
                newSectionTitle,
                true
            );

            const userSectionData = await getClerkUserInfo(userId)

            const authorData = {
                id: userId,
                username: clerkUser?.username,
                rank: userSectionData?.rank,
                avatarUrl: clerkUser?.imageUrl || ""
            };

            const finalNewPara = { ...newPara, author: authorData };

            const updatedParagraphs = [...paragraphs];
            if (insertAtPosition !== null) {
                updatedParagraphs.forEach(p => {
                    if (p.position >= targetPos) p.position += 1;
                });
                updatedParagraphs.splice(targetPos, 0, finalNewPara);
            } else {
                updatedParagraphs.push(finalNewPara);
            }

            setParagraphs(updatedParagraphs.sort((a, b) => a.position - b.position));
            setShowAddModal(false);
            setInsertAtPosition(null);
            setNewSectionContent("");
            setNewSectionRationale("");
        } catch (e) {
            console.error("Failed to add section", e);
        }
    };


    const handleLoadDocVoters = async () => {
        if (documentVoters.length > 0) return;
        const voters = await getDocumentVoters(post.id);
        setDocumentVoters(voters);

        // Fetch History
        const history = await fetchResolutionHistory(post.id);
        setDocumentHistory(history || []);
    };

    const handleLoadDocDebate = async () => {
        if (documentDebate.length > 0) return;

        const debate = await getRationaleComments(post.id, 'document');
        //getEditProposals(post.id)
        setDocumentDebate(debate)

    };

    const handleLoadSectionVoters = async (paragraphId: string) => {
        setIsLoadingVoters(prev => ({ ...prev, [paragraphId]: true }));
        try {
            const voters = await getParagraphVoters(paragraphId);
            setSectionVoters(prev => ({ ...prev, [paragraphId]: voters }));

            const hist = await fetchResolutionHistory(paragraphId)
            setSectionHistory(prev => ({ ...prev, [paragraphId]: hist || [] }));
        } finally {
            setIsLoadingVoters(prev => ({ ...prev, [paragraphId]: false }));
        }
    };

    const handleAddOldSection = async () => {
        if (!newSectionContent.trim()) return;
        try {
            const newPara = await addPolicySection(
                post.id,
                language === 'english' ? newSectionContent : '',
                newSectionRationale,
                paragraphs.length,
                language === 'american' ? newSectionContent : ''
            );

            const authorData = {
                id: userId,
                username: username,
                avatarUrl: clerkUser?.imageUrl || ""
            };

            const finalNewPara = {
                ...newPara,
                author: authorData,
            };

            setParagraphs([...paragraphs, finalNewPara]);
            setShowAddModal(false);
            setNewSectionContent("");
            setNewSectionRationale("");
        } catch (e) {
            console.error("Failed to add section", e);
        }
    };

    const loadSectionComments = async (paragraphId: string) => {
        setSectionLoading(prev => ({ ...prev, [paragraphId]: true }));
        try {
            const comments = await getRationaleComments(paragraphId, 'paragraph');
            setSectionComments(prev => ({ ...prev, [paragraphId]: comments }));
            return comments;
        } catch (error) {
            console.error('Failed to load section comments', error);
            return [];
        } finally {
            setSectionLoading(prev => ({ ...prev, [paragraphId]: false }));
        }
    };

    const loadSectionAmendments = async (paragraphId: string) => {
        setSectionLoading(prev => ({ ...prev, [paragraphId]: true }));
        try {
            const allEdits = await getEditProposals(post.id);
            const edits = allEdits[paragraphId] || [];
            setSectionAmendments(prev => ({ ...prev, [paragraphId]: edits }));
            return edits;
        } catch (error) {
            console.error('Failed to load section amendments', error);
            return [];
        } finally {
            setSectionLoading(prev => ({ ...prev, [paragraphId]: false }));
        }
    };

    const [documentAmendments, setDocumentAmendments] = useState<any[]>([]);

    const loadDocumentAmendments = async (paragraphId: string) => {
        //setSectionLoading(prev => ({ ...prev, [paragraphId]: true }));
        try {
            const allEdits = await getEditProposals(post.id);
            const documentAmendments = allEdits[post.id] || [];
            setDocumentAmendments(documentAmendments)
            return documentAmendments;
        } catch (error) {
            console.error('Failed to load section amendments', error);
            return [];
        } finally {
            //setSectionLoading(prev => ({ ...prev, [paragraphId]: false }));
        }
    };
    const handleAddSectionComment = async (paragraphId: string) => {
        if (!paragraphId || !sectionNewComment.trim()) return;
        if (!userId) {
            alert('Sign in to comment.');
            return;
        }

        setIsSubmittingSectionComment(true);
        try {
            const added = await insertRationaleComment(userId, sectionNewComment.trim(), paragraphId, null);
            setSectionComments(prev => ({
                ...prev,
                [paragraphId]: [...(prev[paragraphId] || []), added]
            }));
            setParagraphs(prev => prev.map((p) => p.id === paragraphId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));
            setSectionNewComment('');
        } catch (error) {
            console.error('Failed to add section comment', error);
            alert('Failed to submit comment.');
        } finally {
            setIsSubmittingSectionComment(false);
        }
    };

    const handleAddDocumentComment = async (policyId: string) => {
        if (!policyId || !sectionNewComment.trim()) return;
        if (!userId) {
            alert('Sign in to comment.');
            return;
        }

        setIsSubmittingSectionComment(true);
        try {
            const added = await insertRationaleComment(userId, sectionNewComment.trim(), null, null, policyId);
            setDocumentDebate([...documentDebate, added])
            //setParagraphs(prev => prev.map((p) => p.id === policyId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));
            setSectionNewComment('');
        } catch (error) {
            console.error('Failed to add section comment', error);
            alert('Failed to submit comment.');
        } finally {
            setIsSubmittingSectionComment(false);
        }
    };

    const handleUpdateSectionComment = async (commentId: string) => {
        try {
            await updateRationaleComment(commentId, tempCommentContent);
            setSectionComments(prev => {
                const updated = { ...prev };
                for (const paragraphId in updated) {
                    updated[paragraphId] = updated[paragraphId].map((c: any) =>
                        c.id === commentId ? { ...c, content: tempCommentContent } : c
                    );
                }
                return updated;
            });
            setEditingCommentId(null);
        } catch (error) {
            console.error('Failed to update comment', error);
            alert('Failed to update comment.');
        }
    };

    const handleUpdateDocumentComment = async (commentId: string) => {
        try {
            await updateRationaleComment(commentId, tempCommentContent);

            setDocumentDebate(documentDebate.map((c: any) => c.id === commentId ? { ...c, content: tempCommentContent } : c));

            setEditingCommentId(null);
        } catch (error) {
            console.error('Failed to update comment', error);
            alert('Failed to update comment.');
        }
    };

    const handleDeleteSectionComment = async (commentId: string) => {
        if (!confirm('Delete this comment?')) return;
        try {
            await deleteRationaleComment(commentId);
            setSectionComments(prev => {
                const updated = { ...prev };
                for (const paragraphId in updated) {
                    updated[paragraphId] = updated[paragraphId].filter((c: any) => c.id !== commentId);
                }
                return updated;
            });
        } catch (error) {
            console.error('Failed to delete comment', error);
            alert('Failed to delete comment.');
        }
    };

    const handleDeleteDocumentComment = async (commentId: string) => {
        if (!confirm('Delete this comment?')) return;
        try {
            await deleteRationaleComment(commentId);
            setDocumentDebate(documentDebate.filter((c: any) => c.id !== id));

        } catch (error) {
            console.error('Failed to delete comment', error);
            alert('Failed to delete comment.');
        }
    };

    const loadSectionVotes = async (paragraphId: string) => {
        setSectionLoading(prev => ({ ...prev, [paragraphId]: true }));
        try {
            // This assumes an endpoint or action that returns profile data for paragraph votes
            const response = await fetch(`/api/votes/paragraph/${paragraphId}`);
            const data = await response.json();
            setSectionVoters(prev => ({ ...prev, [paragraphId]: data }));
        } catch (error) {
            console.error(error);
        } finally {
            setSectionLoading(prev => ({ ...prev, [paragraphId]: false }));
        }
    };

    const loadDocumentVotes = async () => {
        try {
            const response = await fetch(`/api/votes/document/${post.id}`);
            const data = await response.json();
            setDocumentVoters(data);
        } catch (error) {
            console.error(error);
        }
    };

    const toggleSectionDetail = async (paragraphId: string, mode: 'comments' | 'amendments' | 'votes') => {
        const currentMode = openSectionDetails.get(paragraphId);

        if (currentMode === mode) {
            const newOpen = new Map(openSectionDetails);
            newOpen.delete(paragraphId);
            setOpenSectionDetails(newOpen);
            return;
        }

        const newOpen = new Map(openSectionDetails);
        newOpen.set(paragraphId, mode);
        setOpenSectionDetails(newOpen);

        if (mode === 'comments' && !sectionComments[paragraphId]) {
            await loadSectionComments(paragraphId);
        }

        if (mode === 'amendments') {
            // Only set the mode for this specific section to 'new' if not already set
            setSectionAmendmentViewModes(prev => ({
                ...prev,
                [paragraphId]: 'new' //prev[paragraphId] ||
            }));

            if (!sectionAmendments[paragraphId]) {
                await loadSectionAmendments(paragraphId);
            }
        }
        if (mode === 'votes' && !sectionVoters[paragraphId]) await handleLoadSectionVoters(paragraphId);
    };

    const handleSubmitSectionAmendment = async (paragraphId) => {
        if (!showSectionAmendModal) return;
        if (!amendSectionContent.trim() && !amendSectionTranslation.trim()) return;

        try {
            const contentValue = amendSectionContent;
            const translationValue = amendSectionTranslation;

            await submitEditProposal(showSectionAmendModal, contentValue, amendSectionRationale, translationValue, amendSectionTitle);
            await loadSectionAmendments(showSectionAmendModal);
            setParagraphs(prev => prev.map((p) => p.id === showSectionAmendModal ? { ...p, amendmentCount: (p.amendmentCount || 0) + 1 } : p));
            const newOpen = new Map(openSectionDetails);
            newOpen.set(showSectionAmendModal, 'amendments');
            setSectionAmendmentViewModes(prev => ({
                ...prev,
                [paragraphId]: 'new'
            }));
            setOpenSectionDetails(newOpen);
            setShowSectionAmendModal(null);
            setAmendSectionContent('');
            setAmendSectionRationale('');
            setAmendSectionTranslation('');
            setAmendSectionTitle('');
        } catch (error) {
            console.error('Failed to submit amendment', error);
            alert('Failed to submit amendment');
        }
    };

    const handleSubmitTitleAmendment = async () => {
        if (!showSectionAmendModal) return;
        if (!amendSectionContent.trim() && !amendSectionTranslation.trim()) return;

        try {
            const contentValue = amendSectionContent;
            const translationValue = amendSectionTranslation;

            await submitTitleAmendment(showSectionAmendModal, contentValue, amendSectionRationale, translationValue);
            await loadSectionAmendments(showSectionAmendModal);
            setParagraphs(prev => prev.map((p) => p.id === showSectionAmendModal ? { ...p, amendmentCount: (p.amendmentCount || 0) + 1 } : p));
            const newOpen = new Map(openSectionDetails);
            newOpen.set(showSectionAmendModal, 'amendments');
            setOpenSectionDetails(newOpen);
            setShowSectionAmendModal(null);
            setAmendSectionContent('');
            setAmendSectionRationale('');
            setAmendSectionTranslation('');
        } catch (error) {
            console.error('Failed to submit amendment', error);
            alert('Failed to submit amendment');
        }
    };

    const handleDocumentVote = async (type: "upvote" | "downvote") => {
        if (!userId) {
            alert("You must be signed in to vote. Please sign in to continue.");
            return;
        }

        if (post.status === "vetoed") {
            alert("Voting is not allowed on vetoed resolutions.")
            return;
        }

        if (post.status === "effected") {
            //alert("Voting is not allowed on vetoed resolutions.")
            // return;
        }

        setVoteStats((prev) => {
            let up = prev.upvotes;
            let down = prev.downvotes;
            if (prev.userVote === type) {
                if (type === "upvote") up--; else down--;
                return { ...prev, upvotes: up, downvotes: down, userVote: null };
            } else {
                if (type === "upvote") {
                    up++;
                    if (prev.userVote === "downvote") down--;
                } else {
                    down++;
                    if (prev.userVote === "upvote") up--;
                }
                return { ...prev, upvotes: up, downvotes: down, userVote: type };
            }
        });

        try {
            await voteDocument(post.id, type);
            //const data = await fetchDocumentSignatures(post.id);
            //setSignatures(data.filter((s: any) => s.profiles));

            if (post.status === "effected") {
                //  return;
            }
            if (allowAllSignedIn) {
                await fetch("/af_api/bills/check-status/assembly", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ billId: post.id })
                });
            } else if (isHolySeat) {

                await fetch("/af_api/bills/check-status/holy-seat", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ billId: post.id, chamberType })
                });
            }

        } catch (error) {
            console.error("Vote action failed:", error);
        }
    };

    const viewOptions = [
        { value: 'general', label: 'General' },
        { value: 'accepted', label: 'Effected' }, // label: 'Accepted' },
        { value: 'all', label: 'All' },

    ];

    const handleSecond = async () => {
        try {
            await handleSecondDoc(post.id);
            setPost(prev => ({ ...prev, status: 'active' }));

        } catch (error) {
            console.log('error', error)
        }
    }


    const handleParagraphVeto = async (paragraph) => {
        vetoParagraph(paragraph.id);

        // Optimistic UI Update
        setParagraphs((prev) => prev.map((p) => {
            if (p.id !== paragraph.id) return p;

            return {
                ...p,
                status: "vetoed"
            };
        }));


    }

    const handleParagraphEffect = async (paragraph) => {
        effectParagraph(paragraph.id);
        setParagraphs((prev) => prev.map((p) => {
            if (p.id !== paragraph.id) return p;

            return {
                ...p,
                status: "effected"
            };
        }));
    }


    const handleRemoveParagraphEffect = async (paragraph) => {
        removeEffectParagraph(paragraph.id);
        setParagraphs((prev) => prev.map((p) => {
            if (p.id !== paragraph.id) return p;

            return {
                ...p,
                status: "accepted"
            };
        }));
    }


    const handleVote = async (paragraph: PolicyParagraph, type: "upvote" | "downvote") => {
        if (!userId) {
            alert("You must be signed in to vote. Please sign in to continue.");
            return;
        }

        if (post.status === "vetoed") {
            alert("Voting is not allowed on vetoed resolutions.")
            return;
        }

        if (paragraph.status === "vetoed") {
            alert("Voting is not allowed on vetoed sections.")
            return;
        }

        if (post.status === "effected") {
            // alert("Voting is not allowed on effected resolutions.")
            // return;
        }

        if (paragraph.status === "effected") {
            // alert("Voting is not allowed on effected sections.")
            // return;
        }

        const isUp = type === "upvote";
        const currentVote = paragraph.userParagraphVote;

        const quorum = 1;

        const quorumMet = totalVotes >= quorum;

        const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2) + 1;
        let highThreshold = quorumMet ? Math.ceil((totalVotes * 2) / 3) : Math.ceil((quorum * 2) / 3);

        // If the thresholds are equal, increment the two-thirds requirement by one
        if (highThreshold === majorityThreshold) {
            //highThreshold++;
        }
        //if (highThreshold === 1) highThreshold++;



        // Optimistic UI Update
        setParagraphs((prev) => prev.map((p) => {
            if (p.id !== paragraph.id) return p;

            let delta = 0;
            if (currentVote === type) {
                delta = isUp ? -1 : 1;
            } else if (currentVote) {
                delta = isUp ? 2 : -2;
            } else {
                delta = isUp ? 1 : -1;
            }

            const newCount = (p.vote_count || 0) + delta;
            let nextStatus = p.status;


            // Simplified status logic for UI feedback
            if (newCount > 0 && p.status) {
                if (p.status !== "effected") {
                    //nextStatus = "accepted";
                    // if (newCount >= highThreshold && quorumMet) nextStatus = "tentatively_effected";
                }
                if (p.status === "effected") nextStatus = "effected";

            }
            else if (newCount < 0 && quorumMet) {
                //nextStatus = "rejected";
                if (p.status === "effected") nextStatus = "effected";
            }
            //else nextStatus = "proposed";
            if (p.status === "effected") nextStatus = "effected"

            return {
                ...p,
                vote_count: newCount,
                userParagraphVote: currentVote === type ? null : type,
                status: nextStatus
            };
        }));

        try {
            // Use the paragraph's specific ID, not the post.id
            const result = await voteNewParagraph(paragraph.id, isUp, allowAllSignedIn, chamberType);

            if (result) {
                setParagraphs((prev) => prev.map((p) =>
                    p.id === paragraph.id ? { ...p, status: result.newStatus } : p
                ));
            }

            //    setDocumentVoters(prev => ({
            //       ...prev,
            //       [paragraph.id]: [...(prev[paragraph.id] || []), added]
            //   }));
            //    setParagraphs(prev => prev.map((p) => p.id === paragraphId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));

        } catch (e) {
            console.error("Vote failed", e);
        }
    };

    const handleDocumentVeto = async () => {
        vetoDocument(post.id);
        setPost({
            ...post,
            status: "vetoed",
        })
        // router.refresh();
    }
    const handleDocumentEffect = async () => {
        effectDocument(post.id);
        setPost({
            ...post,
            status: "effected",
        })
        //router.refresh();
    }

    const handleRemoveDocumentEffect = async () => {
        removeEffectDocument(post.id);
        setPost({
            ...post,
            status: "in_effect",
        })
        //router.refresh();
    }

    const handleSectionReplacement = (
        paragraphId: string,
        newContent: string,
        newTranslation: string,
        upvotes: number,
        downvotes: number,
        //userVote: "upvote" | "downvote" | null,
        isAccepted: string,
        title: string,
        newRationale: string
    ) => {
        setParagraphs(prev => prev.map(p =>
            p.id === paragraphId
                ? {
                    ...p,
                    content: newContent,
                    translation: newTranslation,
                    //upvotes: upvotes,
                    //downvotes: downvotes,
                    //vote_count: upvotes - downvotes,
                    //userParagraphVote: userVote,
                    status: isAccepted as any, //? 'accepted' : "effected"
                    title: title,
                    rationale: newRationale
                }
                : p
        ));

        loadSectionAmendments(paragraphId);
    };


    const slugify = (text: string) =>
        text.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');


    const handleDocumentTitleReplacement = (
        documentId: string,
        newContent: string,
        newTranslation: string,
        upvotes: number,
        downvotes: number,
        userVote: "upvote" | "downvote" | null,
        isAccepted: string
    ) => {

        if (newContent !== post.title) {
            setPost((prev) => ({ ...prev, title: newContent }));
            router.push(`${resolutionBasePath}/${slugify(post.title)}`)
        }
    };

    // Inside NewDeclarationEditor component

    // 1. Determine Chamber Type

    // 2. Define Rule Helper
    const getChamberRules = (type: string) => {
        switch (type) {
            case 'plus': return { q: 1 + 0.0, num: 3, den: 4, label: "Three-Fourths" };
            case 'pro': return { q: 1 + 0.00, num: 4, den: 5, label: "Four-Fifths" };
            case 'elite': return { q: 1 + 0.000, num: 1, den: 1, label: "Unanimous" };
            default: return { q: 1, num: 2, den: 3, label: "Two-Thirds" };
        }
    };

    const rules = getChamberRules(chamberType);
    const quorum = rules.q;

    const upVotes = voteStats.upvotes;
    const downVotes = voteStats.downvotes;

    const totalVotes = upVotes + downVotes;
    const quorumMet = totalVotes >= quorum;

    const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2) + 1;

    // 3. Calculate the Effect Threshold (High Threshold)
    let highThreshold: number;
    if (chamberType === 'elite') {
        highThreshold = quorumMet ? totalVotes : quorum;
    } else {
        highThreshold = quorumMet
            ? Math.ceil((totalVotes * rules.num) / rules.den)
            : Math.ceil((quorum * rules.num) / rules.den);
    }

    // Ensure high threshold is always at least majority + 1 if they overlap
    if (highThreshold <= majorityThreshold) { // && chamberType !== 'elite'
        //highThreshold++;
    }

    //if (highThreshold === 1) highThreshold++;

    // Rename UI variables from twoThirdsThreshold to highThreshold or effectThreshold



    const hasVoted = !!(voteStats?.userVote);
    const isExecutive = post.tags?.includes("executive") ?? false;

    const isPresident = userId === PRESIDENT_ID;
    const assemblySupports = upVotes > downVotes;
    // Check if already signed/vetoed to prevent double action
    const currentSignStatus = (post as any).is_signed || "unchecked";

    // const quorum = 1;



    //const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2) + 1;
    //let twoThirdsThreshold = quorumMet ? Math.ceil((totalVotes * 2) / 3) : Math.ceil((quorum * 2) / 3);

    // If the thresholds are equal, increment the two-thirds requirement by one
    // if (twoThirdsThreshold === majorityThreshold) {
    //     twoThirdsThreshold++;
    // }

    const [showDocAmendments, setShowDocAmendments] = useState(false);
    const [showAmendTitleModal, setShowAmendTitleModal] = useState<string | null>(null);


    return (
        <div className="-mt-20 min-h-screen text-slate-900 pb-20">
            <div className="max-w-3xl mx-auto px-6 py-12">
                <div className="mb-0 text-center bordfer-b border-amber-200 pb-8 relative">

                    <h1 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 mb-4">{post.title}</h1>


                    <div className="flex justify-center mb-3">

                        {isAdmin && allowAllSignedIn ? (
                            <div className="flex items-center gap-2">
                                {!editingCommittee ? (
                                    <>
                                        <span className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 bg-amber-50">
                                            Committee: {post.committee || "General"}
                                        </span>
                                        <button
                                            onClick={() => setEditingCommittee(true)}
                                            className="ml-2 text-xs px-2 py-1 bg-amber-100 rounded-full text-amber-800 border border-amber-200"
                                        >
                                            Edit
                                        </button>
                                    </>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={selectedCommitteeSlug}
                                            onChange={async (e) => {
                                                const slug = e.target.value;
                                                await handleUpdateCommittee(slug);
                                            }}
                                            className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold tracking-[0.18em] text-amber-800 bg-amber-50"
                                        >
                                            {committeeOptions.map((c) => (
                                                <option key={c.slug} value={c.slug}>{c.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => setEditingCommittee(false)}
                                            className="ml-2 text-xs px-2 py-1 bg-white rounded-full text-amber-800 border border-amber-200"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : allowAllSignedIn && (
                            <span className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 bg-amber-50">
                                Committee: {post.committee || "General"}
                            </span>
                        )}
                    </div>


                    <div className="flex flex-col items-center mb-6 mt-2">
                        <div className="text-center">
                            <p className="text-sm text-slate-700 font-serif mb-1">The {(post.status === "vetoed" || post.status === "effected") ? "Founding Father" : title}</p>
                            <p className={"text-2xl sm:text-3xl font-bold font-serif tracking-wide " +
                                (upVotes !== null && downVotes !== null && post.status !== "vetoed"
                                    ? (upVotes === 0 && downVotes === 0 ? "text-amber-600" : ((quorumMet && upVotes >= highThreshold) ? "text-green-700" : upVotes > downVotes ? "text-green-600" : "text-red-600"))
                                    : "text-slate-400")}>
                                {upVotes !== null && downVotes !== null && post.status !== "vetoed" && post.status !== "effected" ? (
                                    (upVotes === 0 && downVotes === 0) || (!quorumMet && allowAllSignedIn)
                                        ? "IS CONSIDERING"
                                        : (quorumMet && upVotes >= highThreshold) ?
                                            "Tentatively Effects" :
                                            (upVotes > downVotes ? "FAVORS" : "OPPOSES")
                                ) : post.status !== "vetoed" && post.status !== "effected" && "Loading..."}
                                {post.status === "vetoed" && (
                                    <span className="text-red-900">VETOES</span>
                                )}
                                {post.status === "effected" && (
                                    <span className="text-green-900">EFFECTS</span>
                                )}
                            </p>
                            <p className="text-sm text-slate-700 font-serif">this Resolution</p>
                        </div>
                    </div>
                    <div className="flex flex-row mx-auto justify-center gap-2">


                        {isAdmin && post.status !== "vetoed" &&
                            (post.status === "in_effect" || post.status === "tentatively_effected" || post.status === "effected") &&
                            (
                                <div className="-mt-5 mb-3">
                                    <button className='bg-red-800 rounded-full text-slate-100 px-3 hover:bg-red-900 cursor-pointer' onClick={() => handleDocumentVeto()}>
                                        Veto
                                    </button>
                                </div>
                            )}
                        {isAdmin && post.status !== "effected" && post.status !== "vetoed" && (post.status === "in_effect" || upVotes > downVotes) && (
                            <div className="-mt-5 mb-3">
                                <button className='bg-green-800 rounded-full px-3 hover:bg-green-900 text-slate-100 cursor-pointer' onClick={() => handleDocumentEffect()}>
                                    Effect
                                </button>
                            </div>
                        )}
                        {isAdmin && downVotes >= upVotes && post.status === "effected" && ( //post.status === "effected" && false &&
                            <div className="-mt-5 mb-3">
                                <button className='bg-sky-800 rounded-full px-3 hover:bg-sky-900 text-slate-100 cursor-pointer' onClick={() => handleRemoveDocumentEffect()}>
                                    Remove Effect
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center mb-2 justify-center gap-2 text-gray-500 font-serif text-lg">
                        <div className="flex flex-row items-center gap-3">
                            {post.author?.avatarUrl ? (
                                <img src={post.author?.avatarUrl} alt={post.author?.username} className="w-9 h-9 rounded-full border border-amber-200 shadow-sm" />
                            ) : (
                                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                                    <UserIcon size={16} className="text-slate-400" />
                                </div>
                            )}
                            <div className="flex flex-col justify-center text-left">
                                <span className="text-[10px] text-slate-400 font-sans font-bold uppercase leading-tight">Introduced by</span>
                                <div className="-mt-2">
                                    {post.author?.rank && (
                                        <span className={`${post.author?.rank === "Founding Father" ? "bg-purple-300" : "bg-blue-300"} text-[11px] mr-1 px-1 text-stone-900 rounded-md`}>
                                            {post.author?.rank === 'Founding Father' ? "Founding Father" : post.author?.rank}
                                        </span>
                                    )}
                                    <Link href={`/profile/${post.author?.username}`} className="hover:underline">
                                        <span className="text-[11px] text-slate-600 font-sans font-bold">{post.author?.username}</span>
                                    </Link>

                                </div>
                            </div>
                        </div>
                    </div>


                    <div className="mt-2 text-sm text-gray-400 font-serif">


                        <span className={`mr-0 text-[12px] ${post.category === "Moral" ? "text-red-700" : post.category === "General" ? "text-blue-700" : post.category === "Divine" && "text-purple-700"} text-gray-700 font-sans uppercase`}>
                            {post.category === 'General' ? "Human" : post.category === "Divine" ? "Eternal" : post.category === "Moral" ? "Natural" : post.category === "Rule" ? "Political" : post.category === "Opinion" ? "Political" : post.category}
                        </span>
                        {" "}    | On {post.timestamp.toLocaleDateString()}


                        {post.is_starred && (
                            <span

                                className="inline-flex ml-2 items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800 hover:bg-amber-50"

                            >
                                <Star size={14} />
                            </span>
                        )}


                        <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                            <button
                                onClick={() => {
                                    if (!userId) {
                                        alert(language === 'american' ? 'Sign in to propose an amendment.' : 'Sign in to propose an amendment.');
                                        return;
                                    }
                                    if (post.status === 'vetoed') {
                                        alert(language === 'american' ? 'Amendments cannot be added to vetoed resolutions.' : 'Amendments cannot be added to vetoed resolutions.');
                                        return;
                                    }
                                    setShowAmendTitleModal(post.id);
                                    setAmendSectionContent(post.title);
                                    setAmendSectionRationale('');
                                    setAmendSectionTranslation('');
                                }}
                                className="inline-flex ml-2 items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800 hover:bg-amber-50"
                                title="Amend Preamble"
                            >
                                <PenTool size={14} />
                            </button>
                        </Show>

                    </div>


                    {/*   
                {post.status === "draft" && (
                    <div>
                        This declaration has been moved, but it has no second. 
                        {userId !== post.created_by && 
                        <button onClick={handleSecond} className="p-2 ml-2 rounded-xl hover:bg-orange-400 bg-orange-500 text-white">Second</button>
                        }
                       
                    </div>
                )}
                 {post.status === "active" && (
                    <div>
                        This declaration has been moved and seconded. It is brought before the assembly for debate, amendment, and voting.
                        
                       
                    </div>
                )}
                */}

                    <button
                        onClick={() => router.push(`${resolutionBasePath}/${post.slug}/whole`)}
                        className="flex mx-auto mt-3 group/link items-center gap-2 text-amber-800 hover:text-amber-900 transition-colors duration-300 font-sans text-[10px] font-bold uppercase tracking-[0.2em]"
                    >
                        See Resolution Details
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="transition-transform duration-300 transform translate-x-0 group-hover/link:translate-x-1"
                        >
                            <path d="M5 12h14m-7-7 7 7-7 7" />
                        </svg>
                    </button>
                </div>

                <div className={`top-0 z-40 border-y border-amber-300 py-4 ${(showDocDebate) ? 'mb-7' : showDocAmendments ? "mb-3" : "mb-10"}`}>
                    <div className="flex justify-around items-center max-w-4xl mx-auto">
                        <div className="flex-1 flex items-center w-full">
                            <div className="flex w-full items-center justify-between">
                                {allowAllSignedIn ? (
                                    <div className="flex gap-3">

                                        <button
                                            onClick={() => {
                                                if (!userId) {
                                                    alert("Sign in to vote.")
                                                    return;
                                                }
                                                handleDocumentVote("upvote")
                                            }}
                                            className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "upvote"
                                                    ? "bg-green-600 border-green-600 text-white shadow-inner"
                                                    : "bg-transparent border-slate-200 text-slate-600 hover:bg-green-500 hover:border-green-500 hover:text-white hover:shadow-md"}`}
                                        >
                                            {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                        </button>
                                        <div className="flex items-center px-4 text-sm font-bold text-slate-700">{upVotes - downVotes}</div>
                                        <button
                                            onClick={() => {
                                                if (!userId) {
                                                    alert("Sign in to vote.")
                                                    return;
                                                }
                                                handleDocumentVote("downvote")

                                            }}
                                            className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "downvote"
                                                    ? "bg-red-600 border-red-600 text-white shadow-inner"
                                                    : "bg-transparent border-slate-200 text-slate-600 hover:bg-red-500 hover:border-red-500 hover:text-white hover:shadow-md"}`}
                                        >
                                            {!allowAllSignedIn ? 'Nay' : 'No'}
                                        </button>
                                    </div>
                                ) : (
                                    <Show when={hasChamberAccess}
                                        fallback={
                                            <div className="flex gap-3">

                                                <button
                                                    onClick={() => {
                                                        if (!userId) {
                                                            alert("Sign in to vote.")
                                                            return;
                                                        }
                                                        alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                    }}
                                                    className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "upvote"
                                                            ? "bg-green-600 border-green-600 text-white shadow-inner"
                                                            : "bg-transparent border-slate-200 text-slate-600 hover:bg-green-500 hover:border-green-500 hover:text-white hover:shadow-md"}`}
                                                >
                                                    {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                                </button>
                                                <div className="flex items-center px-4 text-sm font-bold text-slate-700">{upVotes - downVotes}</div>
                                                <button
                                                    onClick={() => {
                                                        if (!userId) {
                                                            alert("Sign in to vote.")
                                                            return;
                                                        }
                                                        alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                        return;
                                                    }}
                                                    className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "downvote"
                                                            ? "bg-red-600 border-red-600 text-white shadow-inner"
                                                            : "bg-transparent border-slate-200 text-slate-600 hover:bg-red-500 hover:border-red-500 hover:text-white hover:shadow-md"}`}
                                                >
                                                    {!allowAllSignedIn ? 'Nay' : 'No'}
                                                </button>
                                            </div>

                                        } >


                                        <div className="flex gap-3">

                                            <button
                                                onClick={() => handleDocumentVote("upvote")}
                                                className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "upvote"
                                                        ? "bg-green-600 border-green-600 text-white shadow-inner"
                                                        : "bg-transparent border-slate-200 text-slate-600 hover:bg-green-500 hover:border-green-500 hover:text-white hover:shadow-md"}`}
                                            >
                                                {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                            </button>
                                            <div className="flex items-center px-4 text-sm font-bold text-slate-700">{upVotes - downVotes}</div>
                                            <button
                                                onClick={() => handleDocumentVote("downvote")}
                                                className={`py-3 px-6 font-bold uppercase tracking-widest text-sm rounded transition-all flex items-center justify-center gap-2 border-2 active:scale-95
            ${voteStats.userVote === "downvote"
                                                        ? "bg-red-600 border-red-600 text-white shadow-inner"
                                                        : "bg-transparent border-slate-200 text-slate-600 hover:bg-red-500 hover:border-red-500 hover:text-white hover:shadow-md"}`}
                                            >
                                                {!allowAllSignedIn ? 'Nay' : 'No'}
                                            </button>
                                        </div>
                                    </Show>
                                )}

                                <div className="text-xs flex flex-row text-slate-600 text-right">

                                    <button
                                        onClick={() => {
                                            setShowDocDebate(!showDocDebate);
                                            setShowDocAmendments(false);
                                            setShowDocVoters(false)
                                            handleLoadDocDebate();
                                        }}
                                        className="flex rounded-full bg-white mr-2 border border-amber-300 items-center hover:bg-amber-50  py-4 gap-2 px-3 py-0 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 hover:text-amber-800 transition-colors"
                                    >
                                        <MessageCircle size={14} />
                                        ({post.policyCommentCount})
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowDocAmendments(!showDocAmendments);
                                            setSectionAmendmentViewModes(prev => ({ ...prev, [post.id]: 'new' }))
                                            setShowDocVoters(false)
                                            setShowDocDebate(false);
                                            loadDocumentAmendments(post.id);
                                        }}
                                        className="flex rounded-full bg-white mr-2 border border-amber-300 items-center hover:bg-amber-50  py-4 gap-2 px-3 py-0 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 hover:text-amber-800 transition-colors"
                                    >
                                        <StickyNote size={14} />
                                        ({post.policyAmendmentCount})
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowDocVoters(!showDocVoters);
                                            setShowDocAmendments(false);
                                            setShowDocDebate(false);
                                            handleLoadDocVoters();
                                        }}
                                        className="flex rounded-full bg-white mr-2 border border-amber-300 items-center hover:bg-amber-50  py-4 gap-2 px-3 py-0 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 hover:text-amber-800 transition-colors"
                                    >
                                        <CheckCircle2 size={14} />
                                        {!allowAllSignedIn ? 'Yeas' : 'Ayes'}: {upVotes} | {!allowAllSignedIn ? 'Nays' : 'Noes'}: {downVotes}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 
                 <button
                                        onClick={() => {
                                            setShowDocVoters(!showDocVoters);
                                            handleLoadDocVoters();
                                        }}
                                        className="flex rounded-full bg-white mr-2 border border-amber-300 items-center hover:bg-amber-50  py-4 gap-2 px-3 py-0 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 hover:text-amber-800 transition-colors"
                                    >
                                        <CheckCircle2 size={14} />
                                         {!allowAllSignedIn ? 'Yeas' : 'Ayes'}: {upVotes} | {!allowAllSignedIn ? 'Nays' : 'Noes'}: {downVotes}
                                    </button>

<p className="">{!allowAllSignedIn ? 'Yeas' : 'Ayes'}: {upVotes} | {!allowAllSignedIn ? 'Nays' : 'Noes'}: {downVotes}</p>

                {showDocVoters ? 'Record' : 'Record'}
                           
                
                <p className="-mt-6">{post.objections}</p>
                doc debate loading */}

                <div className="flex flex-col items-center mt-0">

                    {showDocAmendments && (
                        <div className="mt-4 rounded-2xl border w-full mb-10 -mt-6 border-amber-200 bg-amber-50/50 p-4">
                            <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                <span>Resolution Title Amendments</span>
                                {false ? (
                                    <span>Loading...</span>
                                ) : sectionAmendmentViewModes[post.id] === 'new' ? (
                                    <span>{(documentAmendments?.filter(e => e.status === 'pending').length || 0)} new </span>
                                ) : (
                                    <span>{documentAmendments?.length || 0} total</span>
                                )}
                            </div>

                            {/* Tabs for New vs All */}
                            <div className="flex gap-3 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [post.id]: 'new' }))}
                                    className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[post.id] === 'new' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    New
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [post.id]: 'all' }))}
                                    className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[post.id] === 'all' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    All
                                </button>
                            </div>

                            <div className="space-y-3">
                                {(() => {
                                    const allForPreamble = documentAmendments || [];
                                    const filtered = allForPreamble.filter((edit) =>
                                        (sectionAmendmentViewModes[post.id] || 'new') === 'new' ? (edit.status === 'pending' || edit.status === 'accepted') : true
                                    );

                                    if (filtered.length === 0) {
                                        return <div className="text-sm text-slate-500 italic p-4 bg-white/50 rounded-lg">No proposals matching filter.</div>;
                                    }

                                    return filtered.map((edit: any, eIdx: number) => {
                                        // Find the real index for labeling purposes
                                        const globalIndex = allForPreamble.findIndex(e => e.id === edit.id) + 1;
                                        const authorName = edit.author?.username?.username || edit.author?.username || 'Proposer';
                                        const authorRank = edit.author?.rank || null;
                                        const authorImageUrl = edit.author.avatarUrl;

                                        return (
                                            <AmendmentVoteCard
                                                key={edit.id}
                                                edit={{ ...edit, index: globalIndex }}
                                                paragraph={descriptionSection}
                                                userId={userId}
                                                isAdmin={isAdmin}
                                                slug={post.slug}
                                                resolutionBasePath={resolutionBasePath}
                                                onReplacement={(content, trans, up, down, vote, accepted) =>
                                                    handleDocumentTitleReplacement(descriptionSection.id, content, trans, up, down, (vote as "upvote" | "downvote"), accepted)
                                                }
                                                isGeneralAssembly={allowAllSignedIn}
                                                chamberType={chamberType}
                                                isResolutionWhole
                                            />
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}
                    {showDocVoters && (
                        <div className="w-full -mt-6 mb-7 bg-slate-50/80 rounded-2xl border border-slate-200 p-6 animate-in fade-in slide-in-from-top-2">
                            <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-4 text-center">Division of the {title} — {upVotes + downVotes} Vote{upVotes + downVotes !== 1 && "s"} Cast</h3> {/* Final */}
                            <VotingRecordList voters={documentVoters} isAssembly={allowAllSignedIn} history={documentHistory} chamber={chamberType} />
                        </div>
                    )}
                    {showDocDebate && (
                        <div className="mt-0 rounded-2xl mb-10 -mt-5 w-full mx-20 border border-amber-200 bg-amber-50/50 p-4">
                            <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                <span>Resolution Debate</span>
                                {false ? <span>Loading...</span> : <span>{(documentDebate?.length ?? 0)} total</span>}
                            </div>
                            <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                                <div className="mb-4">
                                    {sectionCommentFormSection === post.id ? (
                                        <div className="rounded-2xl bg-white p-4 border border-amber-200">
                                            <textarea
                                                value={sectionNewComment}
                                                onChange={(e) => setSectionNewComment(e.target.value)}
                                                placeholder="Make a speech on the resolution..."
                                                rows={4}
                                                className="w-full p-3 border border-slate-300 rounded-sm bg-slate-50 text-sm focus:border-amber-500 outline-none"
                                            />
                                            <div className="mt-3 flex justify-between">
                                                <button
                                                    onClick={() => {

                                                        handleAddDocumentComment(post.id)
                                                    }}
                                                    className="rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800"
                                                >
                                                    {isSubmittingSectionComment ? 'Concluding…' : 'Conclude Speech'}
                                                </button>
                                                <button onClick={() => setSectionCommentFormSection(null)} className="text-[10px] font-bold uppercase text-slate-500">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                if (descriptionSection.status === "vetoed") {
                                                    alert("Debate is not allowed on vetoed preambles.")
                                                    return;
                                                }
                                                setSectionCommentFormSection(post.id)
                                            }}
                                            className="rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800"
                                        >
                                            Make a speech
                                        </button>
                                    )}
                                </div>
                            </Show>
                            {documentDebate?.length ? (


                                <div className="space-y-3">
                                    {(documentDebate || []).map((comment: any) => {
                                        const commentUser = comment.username?.username || comment.username || 'Anonymous';
                                        const commentRank = comment?.rank || comment.author?.rank || null;
                                        const commentImageUrl = comment.imageUrl || comment.username?.imageUrl;

                                        return (
                                            <div key={comment.id} className="rounded-2xl bg-white p-3 shadow-sm border border-slate-100 group">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center text-[10px] uppercase text-slate-600">
                                                            {commentImageUrl ? (
                                                                <img src={commentImageUrl} alt={commentUser} className="h-full w-full object-cover" />
                                                            ) : (
                                                                <span>{commentUser?.[0] || 'U'}</span>
                                                            )}
                                                        </div>
                                                        {commentRank && (
                                                            <span className="text-[10px] bg-blue-100 px-1 rounded-full text-slate-500 font-bold uppercase">
                                                                {commentRank === 'Founding Father' ? 'Founding Father' : commentRank}
                                                            </span>
                                                        )}
                                                        <Link href={`/profile/${commentUser}`} className="text-xs hover:underline font-bold text-slate-700">{commentUser}</Link>
                                                        <span className="text-[10px] text-slate-400">{new Date(comment.created_at).toLocaleDateString()}</span>
                                                    </div>

                                                    {userId === comment.user_id && (
                                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingCommentId(comment.id);
                                                                    setTempCommentContent(comment.content);
                                                                }}
                                                                className="text-slate-400 hover:text-amber-700"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSectionComment(comment.id)}
                                                                className="text-slate-400 hover:text-red-600"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {editingCommentId === comment.id ? (
                                                    <div className="mt-2">
                                                        <textarea
                                                            value={tempCommentContent}
                                                            onChange={(e) => setTempCommentContent(e.target.value)}
                                                            className="w-full p-3 border border-amber-200 rounded text-sm font-serif outline-none focus:ring-1 focus:ring-amber-500"
                                                            rows={3}
                                                        />
                                                        <div className="flex justify-end gap-2 mt-2">
                                                            <button
                                                                onClick={() => setEditingCommentId(null)}
                                                                className="text-xs text-slate-400 font-bold uppercase tracking-widest"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleUpdateDocumentComment(comment.id)}
                                                                className="text-xs font-bold text-amber-800 uppercase tracking-widest"
                                                            >
                                                                Save
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-slate-700">
                                                        <ExpandableComment content={comment.content} />
                                                    </div>
                                                )}

                                            </div>

                                        );
                                    })}
                                </div>
                            ) : <div className="rounded-2xl bg-white p-4 text-sm text-slate-500">No debate has been held yet.</div>
                            }
                        </div>
                    )}
                </div>

                {/* Presidential Status Display */}
                {currentSignStatus !== "unchecked" && (
                    <div className={`mt-6 p-4 border-2 rounded-lg font-serif text-center ${currentSignStatus === 'signed' ? 'border-green-600 bg-green-50' : 'border-red-600 bg-red-50'}`}>
                        <h3 className={`text-xl font-bold uppercase tracking-widest ${currentSignStatus === 'signed' ? 'text-green-800' : 'text-red-800'}`}>
                            {currentSignStatus === 'signed' ? "Signed by the President" : "Vetoed by the President"}
                        </h3>
                        {post.statement && (
                            <p className="mt-2 italic text-slate-700">"{post.statement}"</p>
                        )}
                    </div>
                )}



                {/*                 {currentSignStatus === "unchecked" && (
                    <div className={`mt-6 p-4 bordefr-2 rounded-lg font-serif text-center `}>
                        
                      
                            <p className="mt-2 italic text-slate-700">President has not checked resolution.</p>
                      
                    </div>
                )}
                    */}


                {/* President Action Button */}
                {isPresident && assemblySupports && false && currentSignStatus === "unchecked" && (
                    <div className="mt-6 flex justify-center">
                        <Button
                            onClick={() => setShowSignModal(true)}
                            className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-2 font-serif"
                        >
                            Executive Action (Sign/Veto)
                        </Button>
                    </div>
                )}


                <div className="flex mb-6 -mt-3 justify-between flex-row">
                    <div className="w-full max-w-[150px]">

                        <CustomSelect
                            value={viewMode}
                            onChange={(value) => setViewMode(value)}
                            options={viewOptions}
                            aria-label="Select view mode"
                            className="font-bold uppercase tracking-widest text-[10px]"
                        />

                    </div>

                </div>
                {descriptionSection && (
                    <div className="mb-14 -mt-1 rounded-3xl border border-amber-300 bg-amber-100/80 p-6 shadow-lg">
                        {/* Top Header Row */}
                        <div className="flex items-center justify-between gap-3 mb-6">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full bg-amber-200/50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-amber-900">
                                    Resolution Preamble
                                </span>
                                <div className="-translate-y-0 flex flex-wrap items-center gap-2">
                                    {descriptionSection.status === "proposed" && (
                                        <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Under Consideration
                                        </span>
                                    )}
                                    {descriptionSection.status === "accepted" && (
                                        <span className="text-[10px] bg-green-100 text-slate-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Favored
                                        </span>
                                    )}
                                    {descriptionSection.status === "tentatively_effected" && (
                                        <span className="text-[10px] bg-green-700 text-slate-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Tentatively Effective
                                        </span>
                                    )}
                                    {descriptionSection.status === "rejected" && (
                                        <span className="text-[10px] bg-red-100 text-gray-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Rejected
                                        </span>
                                    )}
                                    {descriptionSection.status === "vetoed" && (
                                        <span className="text-[10px] bg-red-900 text-gray-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Vetoed
                                        </span>
                                    )}
                                    {descriptionSection.status === "effected" && (
                                        <span className="text-[10px] bg-green-900 text-gray-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                            Effective
                                        </span>
                                    )}
                                </div>
                            </div>
                            <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                                <button
                                    onClick={() => {
                                        if (!userId) {
                                            alert(language === 'american' ? 'Sign in to propose an amendment.' : 'Sign in to propose an amendment.');
                                            return;
                                        }
                                        if (post.status === 'vetoed') {
                                            alert(language === 'american' ? 'Amendments cannot be added to vetoed resolutions.' : 'Amendments cannot be added to vetoed resolutions.');
                                            return;
                                        }
                                        setShowSectionAmendModal(descriptionSection.id);
                                        setAmendSectionContent(descriptionSection.content);
                                        setAmendSectionRationale('');
                                        setAmendSectionTranslation('');
                                    }}
                                    className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800 hover:bg-amber-50"
                                    title="Amend Preamble"
                                >
                                    <PenTool size={14} />
                                </button>
                            </Show>
                        </div>

                        {/* Admin Controls Area */}
                        {isAdmin && (
                            <div className="flex flex-row gap-2 mb-4 -mt-5">
                                {descriptionSection.status !== "vetoed" &&
                                    (descriptionSection.status === "accepted" || descriptionSection.status === "tentatively_effected" || descriptionSection.status === "effected") &&
                                    (
                                        <button
                                            className="bg-red-800 rounded-full text-slate-100 px-3 py-1 text-xs font-bold hover:bg-red-900 cursor-pointer"
                                            onClick={() => handleParagraphVeto(descriptionSection)}
                                        >
                                            Veto
                                        </button>
                                    )}
                                {descriptionSection.status !== "effected" && descriptionSection.status !== "vetoed" &&
                                    (descriptionSection.status === "accepted" || descriptionSection.status === "tentatively_effected") && (
                                        <button
                                            className="bg-green-800 rounded-full px-3 py-1 text-xs font-bold hover:bg-green-900 text-slate-100 cursor-pointer"
                                            onClick={() => handleParagraphEffect(descriptionSection)}
                                        >
                                            Effect
                                        </button>
                                    )}
                                {descriptionSection.status === "effected" && descriptionSection.vote_count <= 0 && (
                                    <button
                                        className="bg-sky-800 rounded-full px-3 py-1 text-xs font-bold hover:bg-sky-900 text-slate-100 cursor-pointer"
                                        onClick={() => handleRemoveParagraphEffect(descriptionSection)}
                                    >
                                        Remove Effect
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Content Area */}
                        <div className="prose prose-lg max-w-none font-serif text-slate-800 text-md leading-loose text-justify">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    h1: ({ ...props }) => <h1 className="text-3xl font-bold mt-0 mb-6 border-b pb-2" {...props} />,
                                    h2: ({ ...props }) => <h2 className="text-2xl font-bold mt-10 mb-4" {...props} />,
                                    h3: ({ ...props }) => <h3 className="text-xl font-bold mt-8 mb-4" {...props} />,
                                    h4: ({ ...props }) => <h4 className="text-lg font-bold mt-6 mb-2" {...props} />,
                                    p: ({ ...props }) => <p className="mb-6 leading-relaxed block" {...props} />,
                                    ul: ({ ...props }) => <ul className="list-disc ml-6 mb-6 space-y-2" {...props} />,
                                    ol: ({ ...props }) => <ol className="list-decimal ml-6 mb-6 space-y-2" {...props} />,
                                    li: ({ ...props }) => <li className="mb-1" {...props} />,
                                    strong: ({ ...props }) => <strong className="font-bold text-black" {...props} />,
                                    em: ({ ...props }) => <em className="italic" {...props} />,
                                }}
                            >
                                {language === 'english' ? (descriptionSection.content || descriptionSection.translation || '') : (descriptionSection.translation || descriptionSection.content || '')}
                            </ReactMarkdown>
                        </div>

                        {/* Bottom Interaction Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-3 -mt-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => router.push(`${resolutionBasePath}/${post.slug}/${descriptionSection.position}`)}
                                    className="flex group/link items-center gap-2 text-amber-900 hover:text-amber-950 transition-colors duration-300 font-sans text-[10px] font-bold uppercase tracking-[0.2em]"
                                >
                                    See Preamble Details
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="transition-transform duration-300 transform translate-x-0 group-hover/link:translate-x-1"
                                    >
                                        <path d="M5 12h14m-7-7 7 7-7 7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => toggleSectionDetail(descriptionSection.id, 'comments')}
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(descriptionSection.id) === 'comments' ? 'border border-amber-800 bg-amber-200 text-amber-900' : 'border border-amber-300 bg-white text-slate-600 hover:bg-amber-50'}`}
                                >
                                    <MessageCircle size={14} />
                                    ({descriptionSection.commentCount ?? 0})
                                </button>
                                <button
                                    onClick={() => toggleSectionDetail(descriptionSection.id, 'amendments')}
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(descriptionSection.id) === 'amendments' ? 'border border-amber-800 bg-amber-200 text-amber-900' : 'border border-amber-300 bg-white text-slate-600 hover:bg-amber-50'}`}
                                >
                                    <StickyNote size={14} />
                                    ({descriptionSection.amendmentCount ?? 0})
                                </button>
                                <button
                                    onClick={() => toggleSectionDetail(descriptionSection.id, 'votes')}
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(descriptionSection.id) === 'votes' ? 'border border-amber-800 bg-amber-200 text-amber-900' : 'border border-amber-300 bg-white text-slate-600 hover:bg-amber-50'}`}
                                    title="Record"
                                >
                                    <CheckCircle2 size={14} />
                                    ({descriptionSection.voterCount ?? 0})
                                </button>
                            </div>

                            {allowAllSignedIn ? (
                                <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                    <button
                                        onClick={() => {
                                            if (!userId) {
                                                alert("Sign in to vote.")
                                                return;
                                            }
                                            handleVote(descriptionSection, 'upvote')
                                        }}
                                        className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                    >
                                        {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                    </button>
                                    <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                        {descriptionSection.vote_count}
                                    </span>
                                    <button
                                        onClick={() => {
                                            if (!userId) {
                                                alert("Sign in to vote.")
                                                return;
                                            }
                                            handleVote(descriptionSection, 'downvote')
                                        }}
                                        className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                    >
                                        {!allowAllSignedIn ? 'Nay' : 'No'}
                                    </button>
                                </div>
                            ) : (
                                <Show when={hasChamberAccess} fallback={
                                    <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                        <button
                                            onClick={() => {
                                                if (!userId) {
                                                    alert("Sign in to vote.")
                                                    return;
                                                }
                                                alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                return;
                                            }}
                                            className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                        >
                                            {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                        </button>
                                        <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                            {descriptionSection.vote_count}
                                        </span>
                                        <button
                                            onClick={() => {
                                                if (!userId) {
                                                    alert("Sign in to vote.")
                                                    return;
                                                }
                                                alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                return;
                                            }}
                                            className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                        >
                                            {!allowAllSignedIn ? 'Nay' : 'No'}
                                        </button>
                                    </div>
                                }>
                                    <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                        <button
                                            onClick={() => handleVote(descriptionSection, 'upvote')}
                                            className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                        >
                                            {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                        </button>
                                        <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                            {descriptionSection.vote_count}
                                        </span>
                                        <button
                                            onClick={() => handleVote(descriptionSection, 'downvote')}
                                            className={`p-2 rounded transition-colors ${descriptionSection.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                        >
                                            {!allowAllSignedIn ? 'Nay' : 'No'}
                                        </button>
                                    </div>
                                </Show>
                            )}


                        </div>

                        {openSectionDetails.get(descriptionSection.id) === 'votes' && (
                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                    <span>Section Voting Record</span>

                                    {isLoadingVoters[descriptionSection.id] ? <span>Fetching...</span> : <span>{sectionVoters[descriptionSection.id]?.length || 0} votes cast</span>}
                                </div>
                                {!isLoadingVoters[descriptionSection.id] && <VotingRecordList voters={sectionVoters[descriptionSection.id] || []} isAssembly={allowAllSignedIn} history={sectionHistory[descriptionSection.id] || []} chamber={chamberType} />}
                            </div>
                        )}

                        {/* Debate Expansion */}
                        {openSectionDetails.get(descriptionSection.id) === 'comments' && (
                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                    <span>Preamble Debate</span>
                                    {sectionLoading[descriptionSection.id] ? <span>Loading...</span> : <span>{(sectionComments[descriptionSection.id]?.length ?? 0)} total</span>}
                                </div>
                                <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                                    <div className="mb-4">
                                        {sectionCommentFormSection === descriptionSection.id ? (
                                            <div className="rounded-2xl bg-white p-4 border border-amber-200">
                                                <textarea
                                                    value={sectionNewComment}
                                                    onChange={(e) => setSectionNewComment(e.target.value)}
                                                    placeholder="Make a speech on the preamble..."
                                                    rows={4}
                                                    className="w-full p-3 border border-slate-300 rounded-sm bg-slate-50 text-sm focus:border-amber-500 outline-none"
                                                />
                                                <div className="mt-3 flex justify-between">
                                                    <button
                                                        onClick={() => {

                                                            handleAddSectionComment(descriptionSection.id)
                                                        }}
                                                        className="rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800"
                                                    >
                                                        {isSubmittingSectionComment ? 'Concluding…' : 'Conclude Speech'}
                                                    </button>
                                                    <button onClick={() => setSectionCommentFormSection(null)} className="text-[10px] font-bold uppercase text-slate-500">Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    if (descriptionSection.status === "vetoed") {
                                                        alert("Debate is not allowed on vetoed preambles.")
                                                        return;
                                                    }
                                                    setSectionCommentFormSection(descriptionSection.id)
                                                }}
                                                className="rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800"
                                            >
                                                Make a speech
                                            </button>
                                        )}
                                    </div>
                                </Show>
                                {sectionComments[descriptionSection.id]?.length ? (


                                    <div className="space-y-3">
                                        {(sectionComments[descriptionSection.id] || []).map((comment: any) => {
                                            const commentUser = comment.username?.username || comment.username || 'Anonymous';
                                            const commentRank = comment?.rank || comment.author?.rank || null;
                                            const commentImageUrl = comment.imageUrl || comment.username?.imageUrl;

                                            return (
                                                <div key={comment.id} className="rounded-2xl bg-white p-3 shadow-sm border border-slate-100 group">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center text-[10px] uppercase text-slate-600">
                                                                {commentImageUrl ? (
                                                                    <img src={commentImageUrl} alt={commentUser} className="h-full w-full object-cover" />
                                                                ) : (
                                                                    <span>{commentUser?.[0] || 'U'}</span>
                                                                )}
                                                            </div>
                                                            {commentRank && (
                                                                <span className="text-[10px] bg-blue-100 px-1 rounded-full text-slate-500 font-bold uppercase">
                                                                    {commentRank === 'Founding Father' ? 'Founding Father' : commentRank}
                                                                </span>
                                                            )}
                                                            <Link href={`/profile/${commentUser}`} className="text-xs hover:underline font-bold text-slate-700">{commentUser}</Link>
                                                            <span className="text-[10px] text-slate-400">{new Date(comment.created_at).toLocaleDateString()}</span>
                                                        </div>

                                                        {userId === comment.user_id && (
                                                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingCommentId(comment.id);
                                                                        setTempCommentContent(comment.content);
                                                                    }}
                                                                    className="text-slate-400 hover:text-amber-700"
                                                                >
                                                                    <Edit2 size={14} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteSectionComment(comment.id)}
                                                                    className="text-slate-400 hover:text-red-600"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {editingCommentId === comment.id ? (
                                                        <div className="mt-2">
                                                            <textarea
                                                                value={tempCommentContent}
                                                                onChange={(e) => setTempCommentContent(e.target.value)}
                                                                className="w-full p-3 border border-amber-200 rounded text-sm font-serif outline-none focus:ring-1 focus:ring-amber-500"
                                                                rows={3}
                                                            />
                                                            <div className="flex justify-end gap-2 mt-2">
                                                                <button
                                                                    onClick={() => setEditingCommentId(null)}
                                                                    className="text-xs text-slate-400 font-bold uppercase tracking-widest"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() => handleUpdateSectionComment(comment.id)}
                                                                    className="text-xs font-bold text-amber-800 uppercase tracking-widest"
                                                                >
                                                                    Save
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-slate-700">
                                                            <ExpandableComment content={comment.content} />
                                                        </div>
                                                    )}

                                                </div>

                                            );
                                        })}
                                    </div>
                                ) : <div className="rounded-2xl bg-white p-4 text-sm text-slate-500">No debate has been held yet.</div>
                                }
                            </div>
                        )}

                        {/* Amendment Expansion */}
                        {openSectionDetails.get(descriptionSection.id) === 'amendments' && (
                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                    <span>Preamble Amendments</span>
                                    {sectionLoading[descriptionSection.id] ? (
                                        <span>Loading...</span>
                                    ) : sectionAmendmentViewModes[descriptionSection.id] === 'new' ? (
                                        <span>{(sectionAmendments[descriptionSection.id]?.filter(e => e.status === 'pending').length || 0)} new </span>
                                    ) : (
                                        <span>{sectionAmendments[descriptionSection.id]?.length || 0} total</span>
                                    )}
                                </div>

                                {/* Tabs for New vs All */}
                                <div className="flex gap-3 mb-4">
                                    <button
                                        type="button"
                                        onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [descriptionSection.id]: 'new' }))}
                                        className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[descriptionSection.id] === 'new' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        New
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [descriptionSection.id]: 'all' }))}
                                        className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[descriptionSection.id] === 'all' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        All
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {(() => {
                                        const allForPreamble = sectionAmendments[descriptionSection.id] || [];
                                        const filtered = allForPreamble.filter((edit) =>
                                            (sectionAmendmentViewModes[descriptionSection.id] || 'new') === 'new' ? edit.status === 'pending' : true
                                        );

                                        if (filtered.length === 0) {
                                            return <div className="text-sm text-slate-500 italic p-4 bg-white/50 rounded-lg">No proposals matching filter.</div>;
                                        }

                                        return filtered.map((edit: any, eIdx: number) => {
                                            // Find the real index for labeling purposes
                                            const globalIndex = allForPreamble.findIndex(e => e.id === edit.id) + 1;
                                            const authorName = edit.author?.username?.username || edit.author?.username || 'Proposer';
                                            const authorRank = edit.author?.rank || null;
                                            const authorImageUrl = edit.author.avatarUrl;

                                            return (
                                                <AmendmentVoteCard
                                                    key={edit.id}
                                                    edit={{ ...edit, index: globalIndex }}
                                                    paragraph={descriptionSection}
                                                    userId={userId}
                                                    isAdmin={isAdmin}
                                                    slug={post.slug}
                                                    resolutionBasePath={resolutionBasePath}
                                                    onReplacement={(content, trans, up, down, vote, accepted, rationale) =>
                                                        handleSectionReplacement(descriptionSection.id, content, trans, up, down, (vote as "upvote" | "downvote"), accepted, rationale)
                                                    }
                                                    isGeneralAssembly={allowAllSignedIn}
                                                    chamberType={chamberType}
                                                />
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="relative h-12 flex -mt-10 items-center justify-center group/insert mb-0">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-amber-100 group-hover/insert:border-amber-300 transition-colors"></div>
                    </div>

                    {allowAllSignedIn ? (
                        <button
                            onClick={() => {
                                if (!userId) {
                                    alert("You must be signed in to add a new section. Please sign in to continue.");
                                    return;
                                }
                                if (post.status === "vetoed") {
                                    alert("New sections are not allowed on vetoed resolutions.")
                                    return;
                                }
                                setInsertAtPosition(1);
                                setShowAddModal(true);
                            }}
                            className="relative z-1 -mt-5 -mb-3 flex items-center gap-2 px-4 py-1 rounded-full bg-white border border-amber-200 text-[10px] font-bold uppercase tracking-widest text-amber-800 opacity-0 group-hover/insert:opacity-100 transition-all hover:bg-amber-50 shadow-sm"
                        >
                            <Plus size={12} />
                            Insert Section Here
                        </button>
                    ) : (
                        <Show when={hasChamberAccess}>
                            <button
                                onClick={() => {
                                    if (!userId) {
                                        alert("You must be signed in to add a new section. Please sign in to continue.");
                                        return;
                                    }
                                    if (post.status === "vetoed") {
                                        alert("New sections are not allowed on vetoed resolutions.")
                                        return;
                                    }
                                    setInsertAtPosition(1);
                                    setShowAddModal(true);
                                }}
                                className="relative z-1 flex items-center gap-2 px-4 py-1 rounded-full bg-white border border-amber-200 text-[10px] font-bold uppercase tracking-widest text-amber-800 opacity-0 group-hover/insert:opacity-100 transition-all hover:bg-amber-50 shadow-sm"
                            >
                                <Plus size={12} />
                                Insert Section Here
                            </button>
                        </Show>
                    )}
                </div>

                {isLoading ? (
                    <div className="text-center py-12 text-slate-400 italic font-serif">Retrieving drafts...</div>
                ) : (
                    <div className="space-y-24">
                        {(() => {
                            const accepted = normalParagraphs.filter((p) => p.status === "accepted");
                            const effected = normalParagraphs.filter(p => p.status === "effected" || p.status === "tentatively_effected");
                            const drafts = normalParagraphs.filter((p) => p.status === "proposed");
                            const rejected = normalParagraphs.filter((p) => p.status === "rejected");

                            const displayList = viewMode === "general"
                                ? [...effected, ...accepted, ...drafts]
                                //: viewMode === 'accepted' ? [...accepted] :
                                : viewMode === 'accepted' ? [...effected] :
                                    //[...accepted, ...drafts, ...rejected];
                                    viewMode === "effected" ? [...effected] :
                                        normalParagraphs;

                            return displayList.map((para, idx) => {
                                // Determine what content to display and if it's italicized
                                const hasContent = !!para.content;
                                const hasTranslation = !!para.translation;
                                let displayContent = '';
                                let isContentMissing = false;
                                let contentLanguage = '';

                                if (language === 'english') {
                                    if (hasContent) {
                                        displayContent = para.content;
                                        contentLanguage = 'English';
                                    } else if (hasTranslation) {
                                        displayContent = para.translation;
                                        contentLanguage = 'American';
                                        isContentMissing = true;
                                    }
                                } else {
                                    if (hasTranslation) {
                                        displayContent = para.translation;
                                        contentLanguage = 'American';
                                    } else if (hasContent) {
                                        displayContent = para.content;
                                        contentLanguage = 'English';
                                        isContentMissing = true;
                                    }
                                }

                                const sectionAmendmentsForPara = sectionAmendments[para.id] || [];
                                const filteredSectionAmendments = sectionAmendmentsForPara.filter((edit) =>
                                    sectionAmendmentViewModes[para.id] === 'new' ? (edit.status === 'pending' || edit.status === 'accepted') : true
                                );

                                const currentViewMode = sectionAmendmentViewModes[para.id] || 'new';
                                const allForPara = sectionAmendments[para.id] || [];
                                const filtered = allForPara.filter((edit) =>
                                    currentViewMode === 'new' ? edit.status === 'pending' : true
                                );

                                return (
                                    <div key={para.id} className="relative group">
                                        <div className="-mb-0">
                                            <div className="flex items-center justify-between gap-3 mb-6">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <span className="text-xs font-bold text-amber-800/60 uppercase tracking-[0.3em] font-sans">
                                                        Section {idx + 1}. {para.title}
                                                    </span>
                                                    <div className="-translate-y-0 flex flex-wrap items-center gap-2">
                                                        {para.status === "proposed" && (
                                                            <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Under Consideration
                                                            </span>
                                                        )}
                                                        {para.status === "accepted" && (
                                                            <span className="text-[10px] bg-green-100 text-slate-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Favored
                                                            </span>
                                                        )}
                                                        {para.status === "tentatively_effected" && (
                                                            <span className="text-[10px] bg-green-700 text-slate-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Tentatively Effective
                                                            </span>
                                                        )}
                                                        {para.status === "rejected" && (
                                                            <span className="text-[10px] bg-red-100 text-gray-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Rejected
                                                            </span>
                                                        )}
                                                        {para.status === "vetoed" && (
                                                            <span className="text-[10px] bg-red-900 text-gray-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Vetoed
                                                            </span>
                                                        )}
                                                        {para.status === "effected" && (
                                                            <span className="text-[10px] bg-green-900 text-gray-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                Effective
                                                            </span>
                                                        )}
                                                        {para.is_starred && (
                                                            <span className="text-[10px]  text-gray-900 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide font-sans">
                                                                <Star />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                                                    <button
                                                        onClick={() => {
                                                            if (!userId) {
                                                                alert('Sign in to propose an amendment.');
                                                                return;
                                                            }
                                                            if (post.status === 'vetoed') {
                                                                alert('Amendments cannot be added to vetoed resolutions.');
                                                                return;
                                                            }
                                                            setShowSectionAmendModal(para.id);
                                                            setAmendSectionContent(para.content);
                                                            setAmendSectionTitle(para.title);
                                                            setAmendSectionRationale('');
                                                            setAmendSectionTranslation('');
                                                        }}
                                                        className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800 hover:bg-amber-50"
                                                        title="Amend"
                                                    >
                                                        <PenTool size={14} />

                                                    </button>
                                                </Show>
                                            </div>
                                            <div className="flex flex-row gap-2">
                                                {isAdmin && para.status !== "vetoed" &&
                                                    (para.status === "accepted" || para.status === "tentatively_effected" || para.status === "effected") &&
                                                    (
                                                        <div className=""  >
                                                            <button className='bg-red-800 -translate-y-6 -mt-4 rounded-full text-slate-100 px-3 hover:bg-red-900 cursor-pointer' onClick={() => handleParagraphVeto(para)}>
                                                                Veto
                                                            </button>
                                                        </div>
                                                    )}
                                                {isAdmin && para.status !== "effected" && para.status !== "vetoed" &&
                                                    (para.status === "accepted" || para.status === "tentatively_effected") && (
                                                        <div className=""  >
                                                            <button className='bg-green-800 -translate-y-6 -mt-4  rounded-full px-3 hover:bg-green-900 text-slate-100 cursor-pointer' onClick={() => handleParagraphEffect(para)}>
                                                                Effect
                                                            </button>
                                                        </div>
                                                    )}
                                                {isAdmin && para.status === "effected" && para.vote_count <= 0 && (
                                                    <div className=""  >
                                                        <button className='bg-sky-800 -translate-y-6 -mt-4 rounded-full px-3 hover:bg-sky-900 text-slate-100 cursor-pointer' onClick={() => handleRemoveParagraphEffect(para)}>
                                                            Remove Effect
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center -mt-4 gap-2 mb-2">

                                                {para.author?.avatarUrl ? (
                                                    <img
                                                        src={para.author.avatarUrl}
                                                        alt={para.author.username}
                                                        className="w-6 h-6 rounded-full border border-amber-100 shadow-sm"
                                                    />
                                                ) : (
                                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                                                        <UserIcon size={12} className="text-slate-400" />
                                                    </div>
                                                )}
                                                <span className="text-[10px] font-bold text-slate-500 font-sans uppercase tracking-wider">
                                                    Introduced by
                                                    {para.author?.rank && (
                                                        <span className={`${para.author?.rank === "Founding Father" ? "bg-purple-300" : "bg-blue-300"} text-[11px] ml-1 px-1 text-stone-900 rounded-md`}>
                                                            {para.author?.rank === 'Founding Father' ? "Founding Father" : para.author?.rank}
                                                        </span>
                                                    )}
                                                    <Link className="hover:underline ml-1" href={`/profile/${para.author?.username}`}>{para.author?.username || 'Unknown User'}</Link>
                                                </span>

                                            </div>
                                            <div className={`prose prose-lg max-w-none font-serif text-slate-800 text-md leading-loose text-justify ${isContentMissing ? '' : ''}`}>
                                                <Markdown content={displayContent} />
                                            </div>
                                            {para.rationale && (
                                                <div className="text-[10px] -mt-4 mb-7 italic text-slate-500 bg-slate-50 p-2 rounded border border-slate-100">
                                                    <p className="">{para.rationale}</p>
                                                </div> 
                                            )}


                                        </div>

                                        <div className="flex flex-wrap items-center justify-between gap-3 -mt-5 mb-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <button
                                                    onClick={() => router.push(`${resolutionBasePath}/` + post.slug + "/" + para.position)}
                                                    className="flex group/link items-center gap-2 text-amber-800 hover:text-amber-900 transition-colors duration-300 font-sans text-[10px] font-bold uppercase tracking-[0.2em]"
                                                >
                                                    See Section Details
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        className="transition-transform duration-300 transform translate-x-0 group-hover/link:translate-x-1"
                                                    >
                                                        <path d="M5 12h14m-7-7 7 7-7 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => toggleSectionDetail(para.id, 'comments')}
                                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(para.id) === 'comments' ? 'border border-amber-800 bg-amber-100 text-amber-900' : 'border border-slate-200 bg-white text-slate-600 hover:bg-amber-50'}`}
                                                    title="Debate"
                                                >
                                                    <MessageCircle size={14} />
                                                    ({para.commentCount ?? 0})
                                                </button>
                                                <button
                                                    onClick={() => toggleSectionDetail(para.id, 'amendments')}
                                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(para.id) === 'amendments' ? 'border border-amber-800 bg-amber-100 text-amber-900' : 'border border-slate-200 bg-white text-slate-600 hover:bg-amber-50'}`}
                                                    title="Amendments"
                                                >
                                                    <StickyNote size={14} />
                                                    ({para.amendmentCount ?? 0})
                                                </button>
                                                <button
                                                    onClick={() => toggleSectionDetail(para.id, 'votes')}
                                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${openSectionDetails.get(para.id) === 'votes' ? 'border border-amber-800 bg-amber-200 text-amber-900' : 'border border-slate-200 bg-white text-slate-600 hover:bg-amber-50'}`}
                                                    title="Record"
                                                >
                                                    <CheckCircle2 size={14} />
                                                    ({para.voterCount ?? 0})
                                                </button>
                                            </div>
                                            {allowAllSignedIn ? (
                                                <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                                    <button
                                                        onClick={() => {
                                                            if (!userId) {
                                                                alert("Sign in to vote.")
                                                                return;
                                                            }
                                                            handleVote(para, 'upvote')
                                                        }}
                                                        className={`p-2 rounded transition-colors ${para.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                                    >
                                                        {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                                    </button>
                                                    <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                                        {para.vote_count}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            if (!userId) {
                                                                alert("Sign in to vote.")
                                                                return;
                                                            }
                                                            handleVote(para, 'downvote')
                                                        }}
                                                        className={`p-2 rounded transition-colors ${para.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                                    >
                                                        {!allowAllSignedIn ? 'Nay' : 'No'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <Show when={hasChamberAccess} fallback={
                                                    <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                                        <button
                                                            onClick={() => {
                                                                if (!userId) {
                                                                    alert("Sign in to vote.")
                                                                    return;
                                                                }
                                                                alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                                return;
                                                            }}
                                                            className={`p-2 rounded transition-colors ${para.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                                        >
                                                            {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                                        </button>
                                                        <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                                            {para.vote_count}
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                if (!userId) {
                                                                    alert("Sign in to vote.")
                                                                    return;
                                                                }
                                                                alert(`Upgrade to the ${chamberAccessLabel} plan to vote here.`);
                                                                return;
                                                            }}
                                                            className={`p-2 rounded transition-colors ${para.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                                        >
                                                            {!allowAllSignedIn ? 'Nay' : 'No'}
                                                        </button>
                                                    </div>
                                                }>
                                                    <div className="flex items-center gap-4 bg-amber-50 p-2 rounded-lg border border-amber-100">
                                                        <button
                                                            onClick={() => handleVote(para, 'upvote')}
                                                            className={`p-2 rounded transition-colors ${para.userParagraphVote === 'upvote' ? 'text-green-600 bg-green-100' : 'text-slate-400 hover:bg-green-100 hover:text-green-600'}`}
                                                        >
                                                            {!allowAllSignedIn ? 'Yea' : 'Aye'}
                                                        </button>
                                                        <span className="font-bold text-lg text-slate-700 min-w-[2ch] text-center">
                                                            {para.vote_count}
                                                        </span>
                                                        <button
                                                            onClick={() => handleVote(para, 'downvote')}
                                                            className={`p-2 rounded transition-colors ${para.userParagraphVote === 'downvote' ? 'text-red-600 bg-red-100' : 'text-slate-400 hover:bg-red-100 hover:text-red-600'}`}
                                                        >
                                                            {!allowAllSignedIn ? 'Nay' : 'No'}
                                                        </button>
                                                    </div>
                                                </Show>
                                            )}



                                        </div>
                                        {openSectionDetails.get(para.id) === 'votes' && (
                                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                                    <span>Section Voting Record</span>

                                                    {isLoadingVoters[para.id] ? <span>Fetching...</span> : <span>{sectionVoters[para.id]?.length || 0} votes cast</span>}
                                                </div>
                                                {!isLoadingVoters[para.id] && <VotingRecordList voters={sectionVoters[para.id] || []} isAssembly={allowAllSignedIn} history={sectionHistory[para.id] || []} chamber={chamberType} />}
                                            </div>
                                        )}
                                        {openSectionDetails.get(para.id) === 'comments' && (
                                            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                                    <span>Section Debate</span>
                                                    {sectionLoading[para.id] ? <span>Loading...</span> : <span>{(sectionComments[para.id]?.length ?? 0)} total</span>}
                                                </div>
                                                <Show when={(has) => allowAllSignedIn || hasChamberAccess(has)}>
                                                    <div className="mb-4">
                                                        {sectionCommentFormSection === para.id ? (
                                                            <div className="rounded-2xl bg-white p-4 border border-slate-200">
                                                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2 block">Add a speech</label>
                                                                <textarea
                                                                    value={sectionNewComment}
                                                                    onChange={(e) => setSectionNewComment(e.target.value)}
                                                                    placeholder="Make a speech on this section..."
                                                                    rows={4}
                                                                    className="w-full p-3 border border-slate-300 rounded-sm bg-slate-50 text-sm text-slate-900 focus:border-amber-500 outline-none"
                                                                />
                                                                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddSectionComment(para.id)}
                                                                        disabled={isSubmittingSectionComment || !sectionNewComment.trim()}
                                                                        className="inline-flex items-center justify-center rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                                    >
                                                                        {isSubmittingSectionComment ? 'Concluding…' : 'Conclude Speech'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSectionCommentFormSection(null)}
                                                                        className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    if (para.status === "vetoed") {
                                                                        alert("Debate is not allowed on vetoed preambles.")
                                                                        return;
                                                                    }
                                                                    setSectionCommentFormSection(para.id)
                                                                }}
                                                                className="inline-flex items-center justify-center rounded-full bg-amber-900 px-4 py-2 text-xs font-bold uppercase text-white hover:bg-amber-800"
                                                            >
                                                                Make a speech
                                                            </button>
                                                        )}
                                                    </div>
                                                </Show>
                                                {sectionComments[para.id]?.length ? (
                                                    <div className="space-y-3">
                                                        {sectionComments[para.id].map((comment: any) => {
                                                            const commentUsername = comment.username?.username || comment.username || 'Anonymous';
                                                            const commentImageUrl = comment.imageUrl || comment.username?.imageUrl;
                                                            const commentRank = comment?.rank || null;
                                                            return (
                                                                <div key={comment.id} className="rounded-2xl bg-white p-3 shadow-sm border border-slate-100 group">
                                                                    <div className="flex items-center gap-3 mb-2 justify-between">
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="h-8 w-8 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center text-[10px] uppercase text-slate-600">
                                                                                {commentImageUrl ? (
                                                                                    <img src={commentImageUrl} alt={commentUsername} className="h-full w-full object-cover" />
                                                                                ) : (
                                                                                    <span>{commentUsername?.[0] || 'U'}</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                {commentRank && (
                                                                                    <span className="text-[10px] bg-blue-100 px-1 rounded-full text-slate-500 font-bold uppercase">{commentRank === "Founding Father" ? "Founding Father" : commentRank}</span>

                                                                                )}

                                                                                <Link href={`/profile/${commentUsername}`} className="text-xs font-bold text-slate-700 hover:underline">
                                                                                    {commentUsername}
                                                                                </Link>

                                                                            </div>
                                                                            <div className="text-[10px] text-slate-400">{new Date(comment.created_at).toLocaleDateString()}</div>
                                                                        </div>
                                                                        {userId === comment.user_id && (
                                                                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                <button onClick={() => { setEditingCommentId(comment.id); setTempCommentContent(comment.content); }} className="text-slate-400 hover:text-amber-700">
                                                                                    <Edit2 size={14} />
                                                                                </button>
                                                                                <button onClick={() => handleDeleteSectionComment(comment.id)} className="text-slate-400 hover:text-red-600">
                                                                                    <Trash2 size={14} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {editingCommentId === comment.id ? (
                                                                        <div className="mt-2">
                                                                            <textarea
                                                                                value={tempCommentContent}
                                                                                onChange={(e) => setTempCommentContent(e.target.value)}
                                                                                className="w-full p-3 border border-amber-200 rounded text-sm font-serif outline-none focus:ring-1 focus:ring-amber-500"
                                                                                rows={3}
                                                                            />
                                                                            <div className="flex justify-end gap-2 mt-2">
                                                                                <button onClick={() => setEditingCommentId(null)} className="text-xs text-slate-400 font-bold uppercase tracking-widest">Cancel</button>
                                                                                <button onClick={() => handleUpdateSectionComment(comment.id)} className="text-xs font-bold text-amber-800 uppercase tracking-widest">Save</button>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-sm text-slate-700"><ExpandableComment content={comment.content} /></div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="rounded-2xl bg-white p-4 text-sm text-slate-500">No debate has been held yet.</div>
                                                )}
                                            </div>
                                        )}
                                        {openSectionDetails.get(para.id) === 'amendments' && (
                                            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                                                <div className="flex items-center justify-between mb-3 text-sm font-bold uppercase tracking-[0.2em] text-slate-700">
                                                    <span>Section Amendments</span>
                                                    {sectionLoading[para.id] ? (
                                                        <span>Loading...</span>
                                                    ) : sectionAmendmentViewModes[para.id] === 'new' ? (
                                                        <span>{filteredSectionAmendments.length} new </span>
                                                    ) : (
                                                        <span>{sectionAmendments[para.id]?.length || 0} total</span>
                                                    )}
                                                </div>

                                                <div className="flex gap-3 mb-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [para.id]: 'new' }))}
                                                        className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[para.id] === 'new' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                                    >
                                                        New
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSectionAmendmentViewModes(prev => ({ ...prev, [para.id]: 'all' }))}
                                                        className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${sectionAmendmentViewModes[para.id] === 'all' ? 'text-amber-800 border-b-2 border-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
                                                    >
                                                        All
                                                    </button>
                                                </div>

                                                {filteredSectionAmendments.length ? (
                                                    <div className="space-y-3">
                                                        {(() => {
                                                            const allSortedForThisPara = [...(sectionAmendments[para.id] || [])].sort(
                                                                (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
                                                            );

                                                            return filteredSectionAmendments.map((edit: any, eIdx: number) => {
                                                                const globalIndex = allSortedForThisPara.findIndex(e => e.id === edit.id) + 1;
                                                                const authorName = edit.author?.username?.username || edit.author?.username || 'Proposer';
                                                                const authorImageUrl = edit.author?.avatarUrl || edit.author?.username?.avatarUrl;
                                                                const authorRank = edit.author?.rank || null;

                                                                return (
                                                                    <AmendmentVoteCard
                                                                        key={edit.id}
                                                                        edit={{ ...edit, index: globalIndex }}
                                                                        paragraph={para}
                                                                        userId={userId}
                                                                        isAdmin={isAdmin}
                                                                        slug={post.slug}
                                                                        resolutionBasePath={resolutionBasePath}
                                                                        onReplacement={(content, trans, up, down, vote, accepted, title, rationale) =>
                                                                            handleSectionReplacement(para.id, content, trans, up, down, accepted, title, rationale) //(vote as "upvote" | "downvote")
                                                                        }
                                                                        isGeneralAssembly={allowAllSignedIn}
                                                                        chamberType={chamberType}
                                                                    />
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                ) : (
                                                    <div className="rounded-2xl bg-white p-4 text-sm text-slate-500">No proposals matching filter.</div>
                                                )}
                                            </div>
                                        )}
                                        {/* Insert between loop item and next */}
                                        {idx + 1 !== displayList.length && (
                                            <div className="relative h-12 flex -mb-22 -mt-0 items-center justify-center group/insert">
                                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                                    <div className="w-full border-t border-amber-100 group-hover/insert:border-amber-300 transition-colors"></div>
                                                </div>
                                                {allowAllSignedIn ? (
                                                    <button
                                                        onClick={() => {
                                                            if (!userId) return alert("Sign in to continue.");
                                                            setInsertAtPosition(idx + 2);
                                                            setShowAddModal(true);
                                                        }}
                                                        className="relative z-1 flex items-center gap-2 px-4 py-1 rounded-full bg-white border border-amber-200 text-[10px] font-bold uppercase tracking-widest text-amber-800 opacity-0 group-hover/insert:opacity-100 transition-all hover:bg-amber-50 shadow-sm"
                                                    >
                                                        <Plus size={12} />
                                                        Insert Section Here
                                                    </button>
                                                ) : (
                                                    <Show when={hasChamberAccess} >
                                                        <button
                                                            onClick={() => {
                                                                if (!userId) return alert("Sign in to continue.");
                                                                setInsertAtPosition(idx + 2);
                                                                setShowAddModal(true);
                                                            }}
                                                            className="relative z-1 flex items-center gap-2 px-4 py-1 rounded-full bg-white border border-amber-200 text-[10px] font-bold uppercase tracking-widest text-amber-800 opacity-0 group-hover/insert:opacity-100 transition-all hover:bg-amber-50 shadow-sm"
                                                        >
                                                            <Plus size={12} />
                                                            Insert Section Here
                                                        </button>
                                                    </Show>
                                                )}

                                            </div>
                                        )}

                                    </div>

                                );

                            });

                        })()}
                    </div>

                )}

                {/* Trigger Button */}
                {!isLoading && (
                    allowAllSignedIn ? (
                        <>

                            <button
                                onClick={() => {
                                    if (!userId) {
                                        alert("You must be signed in to add a new section. Please sign in to continue.");
                                        return;
                                    }
                                    if (post.status === "vetoed") {
                                        alert("New sections are not allowed on vetoed resolutions.")
                                        return;
                                    }
                                    setInsertAtPosition(null);
                                    setShowAddModal(true)

                                }}
                                className="w-full mt-10 py-8 border-2 border-dashed border-amber-200 rounded-lg text-amber-800/60 hover:border-amber-400 hover:text-amber-800 hover:bg-white flex flex-col items-center justify-center gap-3 transition-all font-medium font-serif group"
                            >
                                <Plus size={24} className="group-hover:scale-110 transition-transform" />
                                <span>Propose New Section</span>
                            </button>
                        </>
                    ) : (
                        <Show when={hasChamberAccess} >


                            <button
                                onClick={() => {
                                    if (!userId) {
                                        alert("You must be signed in to add a new section. Please sign in to continue.");
                                        return;
                                    }
                                    if (post.status === "vetoed") {
                                        alert("New sections are not allowed on vetoed resolutions.")
                                        return;
                                    }
                                    setInsertAtPosition(null);
                                    setShowAddModal(true)

                                }}
                                className="w-full mt-10 py-8 border-2 border-dashed border-amber-200 rounded-lg text-amber-800/60 hover:border-amber-400 hover:text-amber-800 hover:bg-white flex flex-col items-center justify-center gap-3 transition-all font-medium font-serif group"
                            >
                                <Plus size={24} className="group-hover:scale-110 transition-transform" />
                                <span>Propose New Section</span>
                            </button>
                        </Show>

                    )

                )}

                {/* Add Section Modal */}
                {showAddModal && (
                    <div className="fixed z-20 overscroll-contain overflow-hidden inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-[#FDFBF7] rounded-sm max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-amber-100">
                            <div className="p-6 border-b border-amber-100 flex justify-between items-center shrink-0">
                                <h2 className="text-xl font-bold text-slate-900 font-serif">Insert New Section</h2>
                                <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                <div className="mb-6">
                                    <label className="text-xs text-slate-600 uppercase font-bold mb-2 block tracking-widest font-sans">Section Title</label>
                                    <input
                                        value={newSectionTitle}
                                        onChange={e => setNewSectionTitle(e.target.value)}
                                        placeholder="Title (optional)..."
                                        //rows={1}
                                        className="w-full p-3 bg-white resize-none border border-slate-300 rounded-sm text-slate-900 focus:border-amber-500 outline-none text-base font-serif leading-loose"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Draft Content</label>
                                    <textarea
                                        value={newSectionContent}
                                        onChange={e => setNewSectionContent(e.target.value)}
                                        placeholder="Type new section content..."
                                        rows={10}
                                        className="w-full p-4 bg-white border border-slate-300 rounded-sm text-slate-900 focus:border-amber-500 outline-none text-base font-serif leading-loose"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs text-slate-400 uppercase font-bold mb-2 block tracking-widest font-sans">Rationale</label>
                                    <input
                                        //rows={1}
                                        value={newSectionRationale}
                                        onChange={e => setNewSectionRationale(e.target.value)}
                                        placeholder="Purpose of this section (optional)..."
                                        className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 text-sm focus:border-amber-500 outline-none font-serif rezize-forbidden"
                                    />
                                </div>
                            </div>

                            <div className="p-6 border-t border-amber-100 flex gap-3 justify-end shrink-0 bg-amber-50/50">
                                <Button variant="ghost" onClick={() => setShowAddModal(false)}>Discard</Button>
                                <Button onClick={handleAddSection} className="bg-amber-800 hover:bg-amber-900 text-white font-sans text-xs uppercase tracking-widest">
                                    Insert Section
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {showSectionAmendModal && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-hidden overscroll-contain">
                        <div className="bg-[#FDFBF7] rounded-sm max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-amber-100">
                            <div className="p-6 border-b border-amber-100 flex justify-between items-center shrink-0">
                                <h2 className="text-xl font-bold text-slate-900 font-serif">Propose Section Amendment</h2>
                                <button onClick={() => setShowSectionAmendModal(null)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                <div className="mb-6">
                                    <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Amendment Title</label>
                                    <input
                                        value={amendSectionTitle}
                                        onChange={e => setAmendSectionTitle(e.target.value)}
                                        placeholder="Draft amendment title..."
                                        className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 focus:border-amber-500 outline-none text-base font-serif leading-loose"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Amendment Content</label>
                                    <textarea
                                        value={amendSectionContent}
                                        onChange={e => setAmendSectionContent(e.target.value)}
                                        placeholder="Draft amendment content..."
                                        rows={8}
                                        className="w-full p-4 bg-white border border-slate-300 rounded-sm text-slate-900 focus:border-amber-500 outline-none text-base font-serif leading-loose"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="text-xs text-slate-400 uppercase font-bold mb-2 block tracking-widest font-sans">Rationale</label>
                                    <input
                                        value={amendSectionRationale}
                                        onChange={e => setAmendSectionRationale(e.target.value)}
                                        placeholder="Why this amendment matters (optional)..."
                                        className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 text-sm focus:border-amber-500 outline-none font-serif"
                                    />
                                </div>
                                {language === 'american' && (
                                    <div className="mb-6">
                                        <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Translation (American)</label>
                                        <input
                                            value={amendSectionTranslation}
                                            onChange={e => setAmendSectionTranslation(e.target.value)}
                                            placeholder="Add a translation for the amendment..."
                                            className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 text-sm focus:border-amber-500 outline-none font-serif"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="p-6 border-t border-amber-100 flex gap-3 justify-end shrink-0 bg-amber-50/50">
                                <Button variant="ghost" onClick={() => setShowSectionAmendModal(null)}>Discard</Button>
                                <Button onClick={() => handleSubmitSectionAmendment(showSectionAmendModal)} className="bg-amber-800 hover:bg-amber-900 text-white font-sans text-xs uppercase tracking-widest">
                                    Submit Amendment
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {showAmendTitleModal && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-hidden overscroll-contain">
                        <div className="bg-[#FDFBF7] rounded-sm max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-amber-100">
                            <div className="p-6 border-b border-amber-100 flex justify-between items-center shrink-0">
                                <h2 className="text-xl font-bold text-slate-900 font-serif">Propose Title Amendment</h2>
                                <button onClick={() => setShowAmendTitleModal(null)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                <div className="mb-6">
                                    <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Proposed Title</label>
                                    <textarea
                                        value={amendSectionContent}
                                        onChange={e => setAmendSectionContent(e.target.value)}
                                        placeholder="Amend title..."
                                        rows={1}
                                        className="w-full resize-none p-4 bg-white border border-slate-300 rounded-sm text-slate-900 focus:border-amber-500 outline-none text-base font-serif leading-loose"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="text-xs text-slate-400 uppercase font-bold mb-2 block tracking-widest font-sans">Rationale</label>
                                    <input
                                        value={amendSectionRationale}
                                        onChange={e => setAmendSectionRationale(e.target.value)}
                                        placeholder="Why this title is to be amended (optional)..."
                                        className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 text-sm focus:border-amber-500 outline-none font-serif"
                                    />
                                </div>
                                {language === 'american' && (
                                    <div className="mb-6">
                                        <label className="text-xs text-amber-800 uppercase font-bold mb-2 block tracking-widest font-sans">Translation (American)</label>
                                        <input
                                            value={amendSectionTranslation}
                                            onChange={e => setAmendSectionTranslation(e.target.value)}
                                            placeholder="Add a translation for the amendment..."
                                            className="w-full p-3 bg-white border border-slate-300 rounded-sm text-slate-900 text-sm focus:border-amber-500 outline-none font-serif"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="p-6 border-t border-amber-100 flex gap-3 justify-end shrink-0 bg-amber-50/50">
                                <Button variant="ghost" onClick={() => setShowAmendTitleModal(null)}>Discard</Button>
                                <Button onClick={handleSubmitTitleAmendment} className="bg-amber-800 hover:bg-amber-900 text-white font-sans text-xs uppercase tracking-widest">
                                    Submit Amendment
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {showSignModal && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-sm max-w-lg w-full p-8 shadow-2xl border-4 border-slate-900">
                            <h2 className="text-2xl font-serif font-bold mb-4 uppercase text-center">Executive Decision</h2>

                            <div className="flex gap-4 mb-6">
                                <button
                                    onClick={() => setPresidentialAction('signed')}
                                    className={`flex-1 py-3 border-2 font-bold ${presidentialAction === 'signed' ? 'bg-green-600 border-green-600 text-white' : 'border-slate-200'}`}
                                >
                                    SIGN
                                </button>
                                <button
                                    onClick={() => setPresidentialAction('vetoed')}
                                    className={`flex-1 py-3 border-2 font-bold ${presidentialAction === 'vetoed' ? 'bg-red-600 border-red-600 text-white' : 'border-slate-200'}`}
                                >
                                    VETO
                                </button>
                            </div>

                            <label className="block text-xs font-bold uppercase mb-2">Presidential Statement</label>
                            <textarea
                                className="w-full p-3 border border-slate-300 font-serif mb-6"
                                rows={4}
                                placeholder="Enter the rationale for your decision..."
                                value={presStatement}
                                onChange={(e) => setPresStatement(e.target.value)}
                            />

                            <div className="flex gap-3">
                                <Button variant="ghost" className="flex-1" onClick={() => setShowSignModal(false)}>Cancel</Button>
                                <Button
                                    disabled={!presidentialAction}
                                    className="flex-1 bg-slate-900 text-white"
                                    onClick={async () => {
                                        if (!presidentialAction) return;
                                        await signOrVetoDocument(post.id, presidentialAction, presStatement);
                                        // Refresh state
                                        setPost({ ...post, is_signed: presidentialAction, presidential_statement: presStatement } as any);
                                        setShowSignModal(false);
                                    }}
                                >
                                    Confirm Action
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};